import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import {
  canPinGroupContent,
  fetchPinnedCommunityGroupPosts,
  useCommunityGroupPostSeenList,
  useRecordCommunityGroupPostsSeen,
} from '../hooks/useCommunityGroupPosts';
import { SeenByDialog } from '../components/SeenByDialog';
import { supabase } from '../services/supabase';
import { hasAdminRole } from '../utils/roles';
import {
  fetchFacilitiesGroupGoogleCalendarEvents,
  isFacilitiesGoogleCalendarEvent,
  mergeGroupCalendarEvents,
} from '../utils/facilitiesGroupCalendar';
import PostRichContent from '../components/social/PostRichContent';
import OriginalPostDialog from '../components/social/OriginalPostDialog';
import { isPostEdited, readOriginalPost, type OriginalPostSnapshot } from '../utils/postEdit';
import {
  checkImageUploadSize,
  checkVideoUploadSize,
  MAX_FEED_IMAGE_UPLOAD_BYTES,
  MAX_VIDEO_UPLOAD_BYTES,
  formatBytesAsMb,
} from '../utils/mediaUploadLimits';
import { uploadVideoToBunny } from '../services/bunnyVideoService';

type GroupDetailTabKey = 'feed' | 'calendar' | 'media' | 'docs' | 'chats' | 'goals';

type GroupDetailRow = {
  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  is_private?: boolean | null;
  current_members?: number | null;
  max_members?: number | null;
};

type GroupFeedItem = {
  id: string;
  content: string;
  authorId: string;
  authorName: string;
  type: string;
  status?: string | null;
  createdAt: string;
  images: string[];
  videoUrl?: string | null;
  likesCount: number;
  commentsCount: number;
  seenCount?: number | null;
  viewerLiked: boolean;
  isPinned?: boolean;
  pinnedAt?: string | null;
  editedAt?: string | null;
  originalPost?: Record<string, unknown> | null;
  videoId?: string | null;
};

type GroupFeedCommentItem = {
  id: string;
  postId: string;
  content: string;
  authorId: string;
  authorName: string;
  createdAt: string;
  replyToId?: string | null;
};

type GroupEventItem = {
  id: string;
  title: string;
  description?: string | null;
  startDate: string;
  endDate: string;
  location?: string | null;
  type: string;
  currentAttendees?: number | null;
  maxAttendees?: number | null;
  isAllDay?: boolean | null;
  createdBy?: string | null;
  readOnly?: boolean;
  source?: 'google';
};

type GroupMediaItem = {
  id: string;
  type: 'image' | 'video' | 'document' | 'link';
  fileName: string;
  fileSize?: number | null;
  uploadedBy?: string | null;
  uploadedAt: string;
  uploadedByName?: string | null;
  description?: string | null;
  url?: string | null;
  storageBucket?: string | null;
  storagePath?: string | null;
};

type GroupChatItem = {
  id: string;
  name: string;
  description?: string | null;
  messageCount: number;
  updatedAt: string;
  isPrivate: boolean;
};

type GroupGoalItem = {
  id: string;
  title: string;
  description?: string | null;
  dueDate: string;
  status: 'pending' | 'achieved' | 'not_achieved';
  createdBy?: string | null;
  createdAt: string;
};

type GroupMembershipState = {
  role: 'admin' | 'moderator' | 'member';
};

type PendingJoinRequest = {
  id: string;
  status: string;
};

const Page = styled.div`
  min-height: 100%;
  padding: clamp(1rem, 2.8vw, 2.35rem);
  background:
    radial-gradient(circle at 12% 10%, color-mix(in srgb, var(--primary) 18%, transparent), transparent 42%),
    radial-gradient(circle at 88% 0%, color-mix(in srgb, var(--success, #166534) 12%, transparent), transparent 35%),
    radial-gradient(circle at 50% 120%, color-mix(in srgb, var(--primary) 8%, transparent), transparent 46%),
    linear-gradient(176deg, color-mix(in srgb, var(--surface) 88%, var(--background)) 0%, var(--background) 62%),
    var(--background);
`;

const Shell = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  display: grid;
  gap: 1.35rem;
`;

const HeaderCard = styled.section`
  position: relative;
  overflow: hidden;
  display: grid;
  gap: 1.3rem;
  padding: clamp(1.35rem, 2.5vw, 2rem);
  border-radius: 32px;
  border: 1px solid color-mix(in srgb, var(--outline) 72%, transparent);
  background: linear-gradient(
    148deg,
    color-mix(in srgb, var(--surface) 97%, transparent),
    color-mix(in srgb, var(--surfaceVariant, var(--surface)) 82%, transparent)
  );
  box-shadow:
    0 24px 56px color-mix(in srgb, black 16%, transparent),
    inset 0 1px 0 color-mix(in srgb, white 42%, transparent),
    inset 0 -1px 0 color-mix(in srgb, black 6%, transparent);

  &::after {
    content: '';
    position: absolute;
    width: 22rem;
    height: 22rem;
    right: -8rem;
    top: -9rem;
    border-radius: 999px;
    background: radial-gradient(circle, color-mix(in srgb, var(--primary) 20%, transparent) 0%, transparent 70%);
    pointer-events: none;
  }

  &::before {
    content: '';
    position: absolute;
    inset: auto auto -4rem -4rem;
    width: 16rem;
    height: 16rem;
    border-radius: 999px;
    background: radial-gradient(circle, color-mix(in srgb, var(--success, #166534) 12%, transparent) 0%, transparent 72%);
    pointer-events: none;
  }
`;

const HeaderTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeaderText = styled.div`
  display: grid;
  gap: 0.65rem;
  max-width: 46rem;
`;

const BackButton = styled.button`
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--outline) 72%, transparent);
  background: color-mix(in srgb, var(--surface) 96%, transparent);
  color: var(--onSurface);
  padding: 0.78rem 1.05rem;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 24px color-mix(in srgb, black 8%, transparent);
  transition: transform 150ms ease, border-color 150ms ease, box-shadow 150ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: color-mix(in srgb, var(--primary) 32%, transparent);
    box-shadow: 0 14px 26px color-mix(in srgb, black 12%, transparent);
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $tone?: 'primary' | 'secondary' }>`
  border-radius: 999px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'primary'
        ? 'color-mix(in srgb, var(--primary) 34%, transparent)'
        : 'color-mix(in srgb, var(--outline) 82%, transparent)'};
  background: ${({ $tone }) =>
    $tone === 'primary'
      ? 'linear-gradient(135deg, var(--primary), color-mix(in srgb, var(--primary) 72%, black))'
      : 'color-mix(in srgb, var(--surface) 92%, transparent)'};
  color: ${({ $tone }) => ($tone === 'primary' ? 'var(--onPrimary)' : 'var(--onSurface)')};
  padding: 0.72rem 1.04rem;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 150ms ease, box-shadow 150ms ease, filter 150ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 20px color-mix(in srgb, black 14%, transparent);
    filter: saturate(1.04);
  }

  &:disabled {
    opacity: 0.72;
    cursor: not-allowed;
  }
`;

const Eyebrow = styled.span`
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--primary);
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.8rem, 4vw, 2.85rem);
  line-height: 1.02;
  letter-spacing: -0.03em;
  color: var(--onSurface);
`;

const Description = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  line-height: 1.7;
  font-size: 0.96rem;
  max-width: 42rem;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.38rem 0.7rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 88%, white);
  color: var(--onSurface);
`;

const StatusStack = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const StatusBanner = styled.div<{ $tone: 'success' | 'error' | 'info' }>`
  padding: 0.7rem 0.85rem;
  border-radius: 14px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'success'
        ? 'color-mix(in srgb, var(--success, #166534) 24%, transparent)'
        : $tone === 'error'
        ? 'color-mix(in srgb, var(--error, #b91c1c) 28%, transparent)'
        : 'color-mix(in srgb, var(--primary) 24%, transparent)'};
  background: ${({ $tone }) =>
    $tone === 'success'
      ? 'color-mix(in srgb, var(--successContainer, #dcfce7) 88%, white)'
      : $tone === 'error'
      ? 'color-mix(in srgb, var(--errorContainer, #fee2e2) 88%, white)'
      : 'color-mix(in srgb, var(--primary) 8%, white)'};
  color: ${({ $tone }) =>
    $tone === 'success'
      ? 'var(--success, #166534)'
      : $tone === 'error'
      ? 'var(--error, #b91c1c)'
      : 'var(--primary)'};
  font-size: 0.84rem;
  line-height: 1.5;
`;

const TabStrip = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  gap: 0.55rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding: 0.2rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 70%, white);

  scrollbar-width: thin;
`;

const TabButton = styled.button<{ $active: boolean }>`
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) =>
      $active
        ? 'color-mix(in srgb, var(--primary) 38%, transparent)'
        : 'color-mix(in srgb, var(--outline) 82%, transparent)'};
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 12%, white), color-mix(in srgb, var(--primary) 8%, transparent))'
      : 'color-mix(in srgb, var(--surface) 92%, transparent)'};
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--onSurfaceVariant)')};
  padding: 0.6rem 0.95rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 150ms ease, border-color 150ms ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const StatGrid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  display: grid;
  gap: 0.45rem;
  padding: 1rem 1.02rem;
  border-radius: 20px;
  border: 1px solid color-mix(in srgb, var(--outline) 74%, transparent);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--surface) 97%, white),
    color-mix(in srgb, var(--surfaceVariant, var(--surface)) 72%, white)
  );
  box-shadow: 0 14px 30px color-mix(in srgb, black 8%, transparent);
`;

const StatLabel = styled.span`
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant);
`;

const StatValue = styled.strong`
  font-size: 1.04rem;
  color: var(--onSurface);
`;

const ContentCard = styled.section`
  display: block;
  padding: 1.35rem;
  border-radius: 28px;
  border: 1px solid color-mix(in srgb, var(--outline) 76%, transparent);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--surface) 97%, transparent),
    color-mix(in srgb, var(--surfaceVariant, var(--surface)) 80%, transparent)
  );
  box-shadow:
    0 24px 50px color-mix(in srgb, black 10%, transparent),
    inset 0 1px 0 color-mix(in srgb, white 36%, transparent);
`;

const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(250px, 0.82fr) minmax(0, 2fr);
  gap: 1.2rem;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
  }
`;

const ContentIntro = styled.aside`
  position: sticky;
  top: 1.1rem;
  align-self: start;
  display: grid;
  gap: 0.9rem;
  padding: 1.15rem;
  border-radius: 22px;
  border: 1px solid color-mix(in srgb, var(--outline) 76%, transparent);
  background: linear-gradient(
    170deg,
    color-mix(in srgb, var(--surfaceVariant, var(--surface)) 78%, white),
    color-mix(in srgb, var(--surface) 96%, transparent)
  );
  box-shadow: 0 18px 34px color-mix(in srgb, black 7%, transparent);

  @media (max-width: 940px) {
    position: static;
  }
`;

const ContentMain = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const ComposerCard = styled.section`
  display: grid;
  gap: 0.8rem;
  padding: 1.15rem;
  border-radius: 22px;
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--surfaceVariant, var(--surface)) 76%, white),
    color-mix(in srgb, var(--surface) 98%, white)
  );
  box-shadow: 0 18px 34px color-mix(in srgb, black 6%, transparent);
`;

const FormGrid = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
`;

const Field = styled.label`
  display: grid;
  gap: 0.35rem;
  font-size: 0.82rem;
  color: var(--onSurfaceVariant);
`;

const FieldLabel = styled.span`
  font-weight: 700;
  color: var(--onSurface);
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem 0.85rem;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surface) 94%, white);
  color: var(--onSurface);
  font: inherit;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.85rem;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surface) 94%, white);
  color: var(--onSurface);
  font: inherit;
  resize: vertical;
`;

const SelectField = styled.select`
  width: 100%;
  padding: 0.75rem 0.85rem;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surface) 94%, white);
  color: var(--onSurface);
  font: inherit;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.84rem;
  color: var(--onSurface);
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  align-items: center;
`;

const HelperText = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  line-height: 1.5;
`;

const MediaUploadRow = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const MediaButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const FilePickerButton = styled.label<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surface) 92%, transparent);
  color: var(--onSurface);
  padding: 0.72rem 1.04rem;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.72 : 1)};

  input {
    display: none;
  }
`;

const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const AttachmentChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 72%, white);
  color: var(--onSurface);
  font-size: 0.8rem;
`;

const AttachmentClearButton = styled.button`
  border: 0;
  background: transparent;
  color: var(--primary);
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
`;

const AttachmentPreview = styled.img`
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--outline) 74%, transparent);
`;

const AttachmentVideoPreview = styled.video`
  width: 100%;
  max-height: 260px;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--outline) 74%, transparent);
  background: #000;
`;

const InlineActions = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const PreviewList = styled.div`
  display: grid;
  gap: 1rem;
`;

const PreviewItem = styled.article<{ $interactive?: boolean; $pinned?: boolean }>`
  position: relative;
  display: grid;
  gap: 0.7rem;
  padding: 1.1rem;
  border-radius: 22px;
  border: 1px solid
    ${({ $pinned }) =>
      $pinned
        ? 'color-mix(in srgb, var(--primary) 28%, var(--outline))'
        : 'color-mix(in srgb, var(--outline) 76%, transparent)'};
  background: ${({ $pinned }) =>
    $pinned
      ? 'color-mix(in srgb, var(--primary) 7%, var(--surface))'
      : `linear-gradient(
    180deg,
    color-mix(in srgb, var(--surface) 98%, white),
    color-mix(in srgb, var(--surfaceVariant, var(--surface)) 70%, white)
  )`};
  box-shadow:
    0 16px 32px color-mix(in srgb, black 7%, transparent),
    inset 0 1px 0 color-mix(in srgb, white 34%, transparent);
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};

  ${({ $pinned }) =>
    $pinned
      ? `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 3px;
      border-radius: 22px 0 0 22px;
      background: linear-gradient(180deg, var(--primaryDark, var(--primary)), var(--primary));
    }
  `
      : ''}

  &:hover {
    ${({ $interactive }) =>
      $interactive
        ? `
      transform: translateY(-2px);
      border-color: color-mix(in srgb, var(--primary) 26%, transparent);
      box-shadow:
        0 22px 40px color-mix(in srgb, black 10%, transparent),
        inset 0 1px 0 color-mix(in srgb, white 34%, transparent);
    `
        : ''}
  }

  &:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--primary) 72%, white);
    outline-offset: 3px;
  }
`;

const PreviewTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const PreviewTitle = styled.h3`
  margin: 0;
  font-size: 1.08rem;
  color: var(--onSurface);
  letter-spacing: -0.02em;
`;

const PreviewMeta = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  line-height: 1.5;
`;

const PreviewBody = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  line-height: 1.6;
`;

const FeedActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  justify-content: space-between;
  padding-top: 0.9rem;
  border-top: 1px solid color-mix(in srgb, var(--outline) 72%, transparent);
`;

const FeedStatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const FeedStat = styled.span`
  font-size: 0.77rem;
  color: var(--onSurfaceVariant);
  padding: 0.34rem 0.62rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--surface) 86%, white);
  border: 1px solid color-mix(in srgb, var(--outline) 72%, transparent);
`;

const FeedActionButton = styled.button<{ $active?: boolean }>`
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) =>
      $active
        ? 'color-mix(in srgb, var(--primary) 36%, transparent)'
        : 'color-mix(in srgb, var(--outline) 82%, transparent)'};
  background: ${({ $active }) =>
    $active
      ? 'color-mix(in srgb, var(--primary) 10%, var(--surface))'
      : 'color-mix(in srgb, var(--surface) 92%, transparent)'};
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--onSurfaceVariant)')};
  padding: 0.42rem 0.72rem;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const FeedCardLead = styled.div`
  display: grid;
  gap: 0.28rem;
`;

const FeedCardHint = styled.span`
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--primary) 82%, var(--onSurface));
`;

const CommentPanel = styled.div`
  display: grid;
  gap: 0.75rem;
  padding: 0.95rem;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  background: color-mix(in srgb, var(--surface) 92%, transparent);
`;

const CommentComposer = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const CommentList = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const CommentItem = styled.div`
  display: grid;
  gap: 0.3rem;
  padding: 0.8rem 0.85rem;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 65%, white);
`;

const CommentMeta = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
`;

const CommentBody = styled.p`
  margin: 0;
  color: var(--onSurface);
  font-size: 0.84rem;
  line-height: 1.55;
`;

const InlineBadgeRow = styled.div`
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const InlineBadge = styled.span<{ $tone?: 'highlight' | 'neutral' | 'success' | 'warning' | 'pinned' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.32rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  background: ${({ $tone }) =>
    $tone === 'highlight' || $tone === 'pinned'
      ? 'color-mix(in srgb, var(--primary) 14%, white)'
      : $tone === 'success'
      ? 'color-mix(in srgb, var(--successContainer, #dcfce7) 85%, white)'
      : $tone === 'warning'
      ? 'color-mix(in srgb, var(--warningContainer, #fef3c7) 85%, white)'
      : 'color-mix(in srgb, var(--surface) 88%, white)'};
  color: ${({ $tone }) =>
    $tone === 'highlight' || $tone === 'pinned'
      ? 'var(--primary)'
      : $tone === 'success'
      ? 'var(--success, #166534)'
      : $tone === 'warning'
      ? 'var(--warning, #92400e)'
      : 'var(--onSurfaceVariant)'};
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'pinned'
        ? 'color-mix(in srgb, var(--primary) 35%, var(--outline))'
        : 'transparent'};
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  color: var(--onSurface);
`;

const SectionBody = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  line-height: 1.65;
`;

const EmptyState = styled.div`
  padding: 1.25rem;
  border-radius: 20px;
  border: 1px dashed color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surface) 90%, transparent);
  color: var(--onSurfaceVariant);
`;

const GROUP_DETAIL_TABS: Array<{ key: GroupDetailTabKey; label: string; summary: string }> = [
  { key: 'feed', label: 'Feed', summary: 'Recent group posts now load here from the existing feed contract.' },
  { key: 'calendar', label: 'Calendar', summary: 'Upcoming group events and attendance snapshots now surface here.' },
  { key: 'media', label: 'Media', summary: 'Shared media uploads are now visible here.' },
  { key: 'docs', label: 'Docs', summary: 'Documents and shared files are now visible here.' },
  { key: 'chats', label: 'Chats', summary: 'Group chat rooms now load here as the entry point to the fuller chat flow.' },
  { key: 'goals', label: 'Goals', summary: 'Group goals and deadlines now load here.' },
];

const parseTab = (value: string | null): GroupDetailTabKey => {
  return GROUP_DETAIL_TABS.some((tab) => tab.key === value) ? (value as GroupDetailTabKey) : 'feed';
};

const getMembersLabelFromCount = (count: number, maxMembers?: number | null) => {
  return maxMembers ? `${count}/${maxMembers} members` : `${count} members`;
};

const getCategoryLabel = (value?: string | null) => {
  if (!value) return 'Other';
  return value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getQueryErrorMessage = (error: unknown) => {
  if (!error) return 'Unable to load this section right now.';
  if (typeof error === 'object' && error && 'message' in error) {
    return String((error as { message?: string }).message || 'Unable to load this section right now.');
  }
  return 'Unable to load this section right now.';
};

const formatDateLabel = (value?: string | null) => {
  if (!value) return 'Date pending';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
};

const formatDateTimeLabel = (value?: string | null) => {
  if (!value) return 'Updated recently';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
};

const formatDateRange = (startDate?: string | null, endDate?: string | null, isAllDay?: boolean | null) => {
  if (!startDate) return 'Date pending';
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  if (isAllDay) {
    return end ? `${dateFormatter.format(start)} to ${dateFormatter.format(end)}` : dateFormatter.format(start);
  }

  const startLabel = `${dateFormatter.format(start)} at ${timeFormatter.format(start)}`;
  if (!end) return startLabel;
  return `${startLabel} to ${dateFormatter.format(end)} at ${timeFormatter.format(end)}`;
};

const formatBytes = (value?: number | null) => {
  if (!value || value <= 0) return 'Size unavailable';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const sanitizeStorageFileName = (value: string) => {
  return String(value || 'file')
    .trim()
    .replace(/[^\w.-]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'file';
};

const normalizeUrlArray = (value: unknown) => {
  if (!Array.isArray(value)) return [] as string[];
  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry): entry is string => entry.length > 0);
};

const resolveContentType = (file: File, type: 'image' | 'video' | 'document') => {
  if (file.type) return file.type;
  const lower = file.name.toLowerCase();
  if (type === 'image') {
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.gif')) return 'image/gif';
    if (lower.endsWith('.webp')) return 'image/webp';
    return 'image/jpeg';
  }
  if (type === 'video') {
    if (lower.endsWith('.mov')) return 'video/quicktime';
    if (lower.endsWith('.webm')) return 'video/webm';
    return 'video/mp4';
  }
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (lower.endsWith('.doc')) return 'application/msword';
  return 'application/octet-stream';
};

const STORAGE_URI_PREFIX = 'storage://';
const GROUP_MEDIA_PRIVATE_BUCKET = 'group-media-assets';
const GROUP_MEDIA_SIGNED_URL_TTL_SECONDS = 3600;

const isStorageObjectMissingError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;
  const maybeMessage = (error as { message?: unknown }).message;
  return typeof maybeMessage === 'string' && maybeMessage.toLowerCase().includes('object not found');
};

const getGroupMediaStorageReference = ({
  url,
  storageBucket,
  storagePath,
}: {
  url?: string | null;
  storageBucket?: string | null;
  storagePath?: string | null;
}) => {
  if (storageBucket && storagePath) {
    return { bucket: storageBucket, path: storagePath };
  }

  if (!url) return null;

  if (url.startsWith(STORAGE_URI_PREFIX)) {
    const rawPath = url.slice(STORAGE_URI_PREFIX.length);
    const slashIndex = rawPath.indexOf('/');
    if (slashIndex <= 0) return null;
    return {
      bucket: rawPath.slice(0, slashIndex),
      path: rawPath.slice(slashIndex + 1),
    };
  }

  try {
    const parsed = new URL(url);
    const markers = [
      '/storage/v1/object/public/',
      '/storage/v1/object/sign/',
      '/storage/v1/object/authenticated/',
    ];

    for (const marker of markers) {
      const index = parsed.pathname.indexOf(marker);
      if (index === -1) continue;
      const remainder = parsed.pathname.slice(index + marker.length);
      const slashIndex = remainder.indexOf('/');
      if (slashIndex <= 0) return null;
      return {
        bucket: remainder.slice(0, slashIndex),
        path: decodeURIComponent(remainder.slice(slashIndex + 1)),
      };
    }
  } catch {
    return null;
  }

  return null;
};

const resolveGroupMediaUrl = async ({
  url,
  storageBucket,
  storagePath,
}: {
  url?: string | null;
  storageBucket?: string | null;
  storagePath?: string | null;
}) => {
  const ref = getGroupMediaStorageReference({ url, storageBucket, storagePath });
  if (!ref) {
    // External doc links and plain http(s) URLs have no storage object.
    if (typeof url === 'string' && url.startsWith(STORAGE_URI_PREFIX)) return null;
    return url ?? null;
  }

  if (ref.bucket === GROUP_MEDIA_PRIVATE_BUCKET) {
    const { data, error } = await supabase.storage
      .from(ref.bucket)
      .createSignedUrl(ref.path, GROUP_MEDIA_SIGNED_URL_TTL_SECONDS);
    if (error) {
      if (isStorageObjectMissingError(error)) {
        console.warn('Group media asset missing from storage', {
          bucket: ref.bucket,
          path: ref.path,
        });
        return null;
      }
      throw error;
    }
    return data?.signedUrl ?? null;
  }

  const { data } = supabase.storage.from(ref.bucket).getPublicUrl(ref.path);
  return data.publicUrl || url || null;
};

const getGoalTone = (status: GroupGoalItem['status']) => {
  if (status === 'achieved') return 'success';
  if (status === 'not_achieved') return 'warning';
  return 'neutral';
};

export const CommunityGroupDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { groupId } = useParams<{ groupId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageGroup = hasAdminRole(roles);
  const seenCardRefs = React.useRef<Record<string, HTMLElement | null>>({});
  const seenPostIdsRef = React.useRef<Set<string>>(new Set());
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [membershipSuccess, setMembershipSuccess] = useState<string | null>(null);
  const [membershipBusyAction, setMembershipBusyAction] = useState<'join' | 'request' | 'leave' | null>(null);
  const [activeSeenPostId, setActiveSeenPostId] = useState<string | null>(null);
  const activeTab = parseTab(searchParams.get('tab'));
  const activeTabMeta = useMemo(
    () => GROUP_DETAIL_TABS.find((tab) => tab.key === activeTab) ?? GROUP_DETAIL_TABS[0],
    [activeTab]
  );

  const groupQuery = useQuery({
    queryKey: ['community-group-detail', groupId],
    enabled: Boolean(groupId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('groups')
        .select('id, name, description, category, is_private, current_members, max_members')
        .eq('id', groupId)
        .single();

      if (error) throw error;
      return data as GroupDetailRow;
    },
  });

  const memberCountQuery = useQuery({
    queryKey: ['community-group-member-count', groupId],
    enabled: Boolean(groupId),
    queryFn: async () => {
      const { count, error } = await supabase
        .from('group_members')
        .select('user_id', { count: 'exact', head: true })
        .eq('group_id', groupId);

      if (error) throw error;
      return count ?? 0;
    },
  });

  const membershipQuery = useQuery({
    queryKey: ['community-group-membership', groupId, user?.id],
    enabled: Boolean(groupId && user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_members')
        .select('role')
        .eq('group_id', groupId)
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) throw error;
      return (data as GroupMembershipState | null) ?? null;
    },
  });

  const pendingJoinRequestQuery = useQuery({
    queryKey: ['community-group-pending-request', groupId, user?.id],
    enabled: Boolean(groupId && user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_invitations')
        .select('id, status')
        .eq('group_id', groupId)
        .eq('invited_user_id', user?.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (error) throw error;
      return (data as PendingJoinRequest | null) ?? null;
    },
  });

  const liveMemberCount = memberCountQuery.data ?? groupQuery.data?.current_members ?? 0;
  const isMember = Boolean(membershipQuery.data);
  const canOpenAdminSettings = canManageGroup || membershipQuery.data?.role === 'admin';
  const canPinContent = canPinGroupContent(membershipQuery.data);
  const canViewSeenStatus = membershipQuery.data?.role === 'admin';
  const hasPendingJoinRequest = Boolean(pendingJoinRequestQuery.data);
  const { data: seenList, isLoading: seenListLoading } = useCommunityGroupPostSeenList(
    activeSeenPostId ?? undefined,
    canViewSeenStatus && Boolean(activeSeenPostId)
  );
  const recordSeen = useRecordCommunityGroupPostsSeen();

  const invalidateGroupMembershipState = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['groups'] }),
      queryClient.invalidateQueries({ queryKey: ['community-group-detail', groupId] }),
      queryClient.invalidateQueries({ queryKey: ['community-group-member-count', groupId] }),
      queryClient.invalidateQueries({ queryKey: ['community-group-membership', groupId, user?.id] }),
      queryClient.invalidateQueries({ queryKey: ['community-group-pending-request', groupId, user?.id] }),
    ]);
  };

  const handleJoinGroup = async () => {
    if (!groupId || !user?.id) return;

    setMembershipBusyAction('join');
    setMembershipError(null);
    setMembershipSuccess(null);

    const { data, error } = await supabase.rpc('add_group_member_txn', {
      p_group_id: groupId,
      p_user_id: user.id,
      p_role: 'member',
      p_resolve_pending_invitations: true,
    });

    if (error) {
      setMembershipError(error.message || 'Unable to join this group right now.');
      setMembershipBusyAction(null);
      return;
    }

    const resolution = Array.isArray(data) ? data[0] : null;
    setMembershipSuccess(
      String(resolution?.outcome || '').toLowerCase() === 'already-member'
        ? 'You are already a member of this group.'
        : 'You joined the group.'
    );
    setMembershipBusyAction(null);
    await invalidateGroupMembershipState();
  };

  const handleRequestToJoinGroup = async () => {
    if (!groupId || !user?.id) return;

    setMembershipBusyAction('request');
    setMembershipError(null);
    setMembershipSuccess(null);

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 30);

    const { data: existingRequest, error: existingRequestError } = await supabase
      .from('group_invitations')
      .select('id, status')
      .eq('group_id', groupId)
      .eq('invited_user_id', user.id)
      .maybeSingle();

    if (existingRequestError) {
      setMembershipError(existingRequestError.message || 'Unable to prepare join request.');
      setMembershipBusyAction(null);
      return;
    }

    if (existingRequest?.status === 'pending') {
      setMembershipSuccess('Your join request is already pending review.');
      setMembershipBusyAction(null);
      await invalidateGroupMembershipState();
      return;
    }

    if (existingRequest?.id) {
      const { error: updateError } = await supabase
        .from('group_invitations')
        .update({
          status: 'pending',
          message: 'User requested to join this private group',
          invited_by: user.id,
          created_at: new Date().toISOString(),
          expires_at: expiresAt.toISOString(),
        })
        .eq('id', existingRequest.id);

      if (updateError) {
        setMembershipError(updateError.message || 'Unable to send join request.');
        setMembershipBusyAction(null);
        return;
      }
    } else {
      const { error: insertError } = await supabase.from('group_invitations').insert({
        group_id: groupId,
        invited_user_id: user.id,
        invited_by: user.id,
        status: 'pending',
        message: 'User requested to join this private group',
        created_at: new Date().toISOString(),
        expires_at: expiresAt.toISOString(),
      });

      if (insertError) {
        setMembershipError(insertError.message || 'Unable to send join request.');
        setMembershipBusyAction(null);
        return;
      }
    }

    setMembershipSuccess('Join request sent. Group admins will review it soon.');
    setMembershipBusyAction(null);
    await invalidateGroupMembershipState();
  };

  const handleLeaveGroup = async () => {
    if (!groupId || !user?.id) return;
    if (!window.confirm('Leave this group?')) return;

    setMembershipBusyAction('leave');
    setMembershipError(null);
    setMembershipSuccess(null);

    const { error } = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', user.id);

    if (error) {
      setMembershipError(error.message || 'Unable to leave this group right now.');
      setMembershipBusyAction(null);
      return;
    }

    setMembershipSuccess('You left the group.');
    setMembershipBusyAction(null);
    await invalidateGroupMembershipState();
  };

  const currentUserDisplayName =
    (typeof profile?.full_name === 'string' ? profile.full_name.trim() : '') ||
    (typeof user?.user_metadata?.full_name === 'string' ? user.user_metadata.full_name.trim() : '') ||
    user?.email ||
    'Unknown user';
  const canViewGroupContent = Boolean(groupQuery.data) && (!groupQuery.data?.is_private || isMember || canManageGroup);
  const canCreateGroupContent = isMember || canManageGroup;
  const [tabFeedback, setTabFeedback] = useState<
    Partial<Record<GroupDetailTabKey, { tone: 'success' | 'error' | 'info'; text: string }>>
  >({});
  const [editingFeedPost, setEditingFeedPost] = useState<GroupFeedItem | null>(null);
  const [originalSnapshot, setOriginalSnapshot] = useState<OriginalPostSnapshot | null>(null);
  const [feedDraft, setFeedDraft] = useState('');
  const [feedType, setFeedType] = useState<'post' | 'announcement' | 'prayer' | 'praise' | 'testimony'>('post');
  const [isFeedScheduled, setIsFeedScheduled] = useState(false);
  const [feedScheduledFor, setFeedScheduledFor] = useState('');
  const [isSubmittingFeed, setIsSubmittingFeed] = useState(false);
  const [feedImageUrl, setFeedImageUrl] = useState('');
  const [feedImageName, setFeedImageName] = useState('');
  const [feedImageSize, setFeedImageSize] = useState(0);
  const [feedVideoUrl, setFeedVideoUrl] = useState('');
  const [feedVideoId, setFeedVideoId] = useState('');
  const [feedVideoName, setFeedVideoName] = useState('');
  const [feedVideoSize, setFeedVideoSize] = useState(0);
  const [isUploadingFeedImage, setIsUploadingFeedImage] = useState(false);
  const [isUploadingFeedVideo, setIsUploadingFeedVideo] = useState(false);
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);
  const [pinningPostId, setPinningPostId] = useState<string | null>(null);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaDescriptionDraft, setMediaDescriptionDraft] = useState('');
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentDescriptionDraft, setDocumentDescriptionDraft] = useState('');
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [deletingMediaId, setDeletingMediaId] = useState<string | null>(null);
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescriptionDraft, setEventDescriptionDraft] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventType, setEventType] = useState<'meeting' | 'prayer' | 'study' | 'fellowship' | 'outreach' | 'worship' | 'other'>('meeting');
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventAllDay, setEventAllDay] = useState(false);
  const [isSubmittingEvent, setIsSubmittingEvent] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [goalTitle, setGoalTitle] = useState('');
  const [goalDescriptionDraft, setGoalDescriptionDraft] = useState('');
  const [goalDueDate, setGoalDueDate] = useState('');
  const [isSubmittingGoal, setIsSubmittingGoal] = useState(false);
  const [updatingGoalId, setUpdatingGoalId] = useState<string | null>(null);
  const [deletingGoalId, setDeletingGoalId] = useState<string | null>(null);
  const [activeCommentPostId, setActiveCommentPostId] = useState<string | null>(null);
  const [groupCommentDraft, setGroupCommentDraft] = useState('');
  const [isSubmittingGroupComment, setIsSubmittingGroupComment] = useState(false);
  const [likingGroupPostId, setLikingGroupPostId] = useState<string | null>(null);

  const setFeedback = (
    tab: GroupDetailTabKey,
    tone: 'success' | 'error' | 'info',
    text: string
  ) => {
    setTabFeedback((previous) => ({
      ...previous,
      [tab]: { tone, text },
    }));
  };

  const clearFeedback = (tab: GroupDetailTabKey) => {
    setTabFeedback((previous) => {
      const next = { ...previous };
      delete next[tab];
      return next;
    });
  };

  const invalidateGroupSlice = async (...queryKeys: Array<readonly unknown[]>) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['groups'] }),
      ...queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ]);
  };

  const clearFeedMedia = () => {
    setFeedImageUrl('');
    setFeedImageName('');
    setFeedImageSize(0);
    setFeedVideoUrl('');
    setFeedVideoId('');
    setFeedVideoName('');
    setFeedVideoSize(0);
  };

  const uploadGroupPostImageFile = async (file: File) => {
    if (!groupId || !user?.id) {
      throw new Error('You must be signed in to upload files.');
    }

    const sizeCheck = checkImageUploadSize(file.size);
    if (!sizeCheck.ok) {
      throw new Error(sizeCheck.reason);
    }

    const safeName = sanitizeStorageFileName(file.name || `image-${Date.now()}`);
    const extension = safeName.includes('.') ? safeName.split('.').pop() : '';
    const filePath = `group-posts/${groupId}/${user.id}_${Date.now()}${extension ? `.${extension}` : ''}`;

    const { error: uploadError } = await supabase.storage.from('post-images').upload(filePath, file, {
      contentType: resolveContentType(file, 'image'),
      upsert: true,
    });

    if (uploadError) {
      throw uploadError;
    }

    const { data: publicUrlData } = supabase.storage.from('post-images').getPublicUrl(filePath);
    return {
      url: publicUrlData.publicUrl,
      path: filePath,
      filename: safeName,
      size: file.size,
    };
  };

  const handleFeedImageSelected = async (file?: File | null) => {
    if (!file) return;
    setIsUploadingFeedImage(true);
    clearFeedback('feed');
    try {
      const uploaded = await uploadGroupPostImageFile(file);
      setFeedImageUrl(uploaded.url);
      setFeedImageName(uploaded.filename);
      setFeedImageSize(uploaded.size);
      setFeedback('feed', 'success', 'Photo attached.');
    } catch (error) {
      setFeedback('feed', 'error', getQueryErrorMessage(error));
    } finally {
      setIsUploadingFeedImage(false);
    }
  };

  const handleFeedVideoSelected = async (file?: File | null) => {
    if (!file) return;
    if (!user?.id) {
      setFeedback('feed', 'error', 'You must be signed in to upload a video.');
      return;
    }

    setIsUploadingFeedVideo(true);
    clearFeedback('feed');
    try {
      const uploadResult = await uploadVideoToBunny(
        file,
        {
          title: feedDraft.trim() || file.name || 'Group video',
          description: feedDraft.trim() || 'Video uploaded with group post',
          category: 'Groups',
        },
        user.id
      );
      if (!uploadResult.success || !uploadResult.data) {
        throw new Error(uploadResult.error || 'Failed to upload video to Bunny.');
      }
      setFeedVideoUrl(uploadResult.data.videoUrl);
      setFeedVideoId(uploadResult.data.videoId);
      setFeedVideoName(file.name || `video-${Date.now()}.mp4`);
      setFeedVideoSize(uploadResult.data.size || file.size);
      setFeedback('feed', 'success', 'Video attached. Processing will finish in the background.');
    } catch (error) {
      setFeedback('feed', 'error', getQueryErrorMessage(error));
    } finally {
      setIsUploadingFeedVideo(false);
    }
  };

  const uploadGroupFile = async (file: File, type: 'image' | 'video' | 'document', description: string) => {
    if (!groupId || !user?.id) {
      throw new Error('You must be signed in to upload files.');
    }

    if (type === 'video') {
      const sizeCheck = checkVideoUploadSize(file.size);
      if (!sizeCheck.ok) {
        throw new Error(sizeCheck.reason);
      }
    }

    const safeName = sanitizeStorageFileName(file.name || `${type}-${Date.now()}`);
    const extension = safeName.includes('.') ? safeName.split('.').pop() : '';
    const scopePrefix = type === 'document' ? 'group-documents' : 'group-media';
    const filePath = `${scopePrefix}/${groupId}/uncategorized/${user.id}_${Date.now()}${extension ? `.${extension}` : ''}`;

    const { error: uploadError } = await supabase.storage.from('post-images').upload(filePath, file, {
      contentType: resolveContentType(file, type),
      upsert: true,
    });

    if (uploadError) {
      throw uploadError;
    }

    const { data: publicUrlData } = supabase.storage.from('post-images').getPublicUrl(filePath);
    const { error: insertError } = await supabase.from('group_media').insert({
      group_id: groupId,
      type,
      size: file.size,
      description: description.trim() || null,
      uploaded_by: user.id,
      uploaded_by_name: currentUserDisplayName,
      url: publicUrlData.publicUrl,
      storage_bucket: 'post-images',
      storage_path: filePath,
      uploaded_at: new Date().toISOString(),
      filename: safeName,
    });

    if (insertError) {
      throw insertError;
    }
  };

  const handleCreatePost = async () => {
    if (!groupId || !user?.id) return;
    const normalizedContent = feedDraft.trim();
    const hasMedia = Boolean(feedImageUrl || feedVideoUrl);
    if (!normalizedContent && !hasMedia) {
      setFeedback('feed', 'error', 'Add a message, photo, or video before publishing.');
      return;
    }

    if (feedType === 'prayer' && hasMedia) {
      setFeedback('feed', 'error', 'Prayer requests cannot include photos or videos.');
      return;
    }

    if (isFeedScheduled && (!feedScheduledFor || new Date(feedScheduledFor).getTime() <= Date.now())) {
      setFeedback('feed', 'error', 'Please choose a date and time in the future.');
      return;
    }

    setIsSubmittingFeed(true);
    clearFeedback('feed');

    if (editingFeedPost) {
      const { error } = await supabase.rpc('edit_group_post', {
        p_post_id: editingFeedPost.id,
        p_patch: {
          content: normalizedContent,
          images: feedImageUrl
            ? [feedImageUrl, ...(editingFeedPost.images ?? []).filter((url) => url !== feedImageUrl)]
            : [],
          video_url: feedVideoUrl || null,
          video_id: feedVideoUrl ? feedVideoId || null : null,
          video_file_name: feedVideoUrl ? feedVideoName || null : null,
          video_file_size: feedVideoUrl ? feedVideoSize || null : null,
          video_processing_status: feedVideoUrl ? (feedVideoId ? 'processing' : 'ready') : null,
        },
      });
      if (error) {
        setFeedback('feed', 'error', error.message || 'Unable to update this post.');
        setIsSubmittingFeed(false);
        return;
      }
      setFeedDraft('');
      setFeedType('post');
      setEditingFeedPost(null);
      clearFeedMedia();
      setFeedback('feed', 'success', 'Post updated.');
      setIsSubmittingFeed(false);
      await invalidateGroupSlice(['community-group-feed', groupId], ['community-group-library', groupId]);
      return;
    }

    const scheduledForIso = isFeedScheduled && feedScheduledFor ? new Date(feedScheduledFor).toISOString() : null;
    const images = feedImageUrl ? [feedImageUrl] : [];

    const { data, error } = await supabase
      .from('group_posts')
      .insert({
        group_id: groupId,
        content: normalizedContent,
        type: feedType,
        images,
        video_url: feedVideoUrl || null,
        video_id: feedVideoUrl ? feedVideoId || null : null,
        video_file_name: feedVideoUrl ? feedVideoName || null : null,
        video_file_size: feedVideoUrl ? feedVideoSize || null : null,
        video_processing_status: feedVideoUrl ? (feedVideoId ? 'processing' : 'ready') : null,
        author_id: user.id,
        author_name: currentUserDisplayName,
        status: scheduledForIso ? 'scheduled' : 'published',
        scheduled_for: scheduledForIso,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        likes: 0,
        comments: 0,
      })
      .select('id, created_at')
      .single();

    if (error) {
      setFeedback('feed', 'error', error.message || 'Unable to publish this post.');
      setIsSubmittingFeed(false);
      return;
    }

    const mediaRows = [
      ...(feedImageUrl
        ? [
            {
              group_id: groupId,
              type: 'image' as const,
              size: feedImageSize || 0,
              description: null,
              uploaded_by: user.id,
              uploaded_by_name: currentUserDisplayName,
              url: feedImageUrl,
              uploaded_at: data?.created_at || new Date().toISOString(),
              filename: feedImageName || `image-${Date.now()}.jpg`,
            },
          ]
        : []),
      ...(feedVideoUrl
        ? [
            {
              group_id: groupId,
              type: 'video' as const,
              size: feedVideoSize || 0,
              description: null,
              uploaded_by: user.id,
              uploaded_by_name: currentUserDisplayName,
              url: feedVideoUrl,
              uploaded_at: data?.created_at || new Date().toISOString(),
              filename: feedVideoName || `video-${Date.now()}.mp4`,
            },
          ]
        : []),
    ];

    if (mediaRows.length > 0) {
      const { error: mediaInsertError } = await supabase.from('group_media').insert(mediaRows);
      if (mediaInsertError) {
        console.warn('Error saving group post media rows:', mediaInsertError);
      }
    }

    setFeedDraft('');
    setFeedType('post');
    setIsFeedScheduled(false);
    setFeedScheduledFor('');
    clearFeedMedia();
    setFeedback(
      'feed',
      'success',
      scheduledForIso ? `Post scheduled for ${new Date(scheduledForIso).toLocaleString()}.` : 'Post published.'
    );
    setIsSubmittingFeed(false);
    await invalidateGroupSlice(['community-group-feed', groupId], ['community-group-library', groupId]);
  };

  const handleDeletePost = async (post: GroupFeedItem) => {
    if (!user?.id) return;
    if (!window.confirm('Delete this post?')) return;

    setDeletingPostId(post.id);
    clearFeedback('feed');

    const { error } = await supabase.from('group_posts').delete().eq('id', post.id);

    if (error) {
      setFeedback('feed', 'error', error.message || 'Unable to delete this post.');
      setDeletingPostId(null);
      return;
    }

    setFeedback('feed', 'success', 'Post deleted.');
    setDeletingPostId(null);
    await invalidateGroupSlice(['community-group-feed', groupId]);
  };

  const handleTogglePostPinned = async (post: GroupFeedItem) => {
    if (!canPinContent) return;

    setPinningPostId(post.id);
    clearFeedback('feed');
    const nextPinned = !Boolean(post.isPinned);
    const { error } = await supabase.rpc('set_group_post_pinned', {
      p_post_id: post.id,
      p_pinned: nextPinned,
    });

    if (error) {
      setFeedback('feed', 'error', error.message || 'Unable to update pin.');
      setPinningPostId(null);
      return;
    }

    updateFeedPost(post.id, (current) => ({
      ...current,
      isPinned: nextPinned,
      pinnedAt: nextPinned ? new Date().toISOString() : null,
    }));
    setFeedback('feed', 'success', nextPinned ? 'Post pinned.' : 'Post unpinned.');
    setPinningPostId(null);
    await invalidateGroupSlice(['community-group-feed', groupId]);
  };

  const handleUploadMedia = async () => {
    if (!mediaFile) {
      setFeedback('media', 'error', 'Select an image or video file first.');
      return;
    }

    const nextType = mediaFile.type.startsWith('video/') ? 'video' : 'image';
    setIsUploadingMedia(true);
    clearFeedback('media');

    try {
      await uploadGroupFile(mediaFile, nextType, mediaDescriptionDraft);
      setMediaFile(null);
      setMediaDescriptionDraft('');
      setFeedback('media', 'success', 'Media uploaded.');
      await invalidateGroupSlice(['community-group-library', groupId]);
    } catch (error) {
      setFeedback('media', 'error', getQueryErrorMessage(error));
    } finally {
      setIsUploadingMedia(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!documentFile) {
      setFeedback('docs', 'error', 'Select a document file first.');
      return;
    }

    setIsUploadingDocument(true);
    clearFeedback('docs');

    try {
      await uploadGroupFile(documentFile, 'document', documentDescriptionDraft);
      setDocumentFile(null);
      setDocumentDescriptionDraft('');
      setFeedback('docs', 'success', 'Document uploaded.');
      await invalidateGroupSlice(['community-group-library', groupId]);
    } catch (error) {
      setFeedback('docs', 'error', getQueryErrorMessage(error));
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const handleDeleteMedia = async (item: GroupMediaItem, tab: 'media' | 'docs') => {
    if (!window.confirm(`Delete ${item.fileName}?`)) return;

    setDeletingMediaId(item.id);
    clearFeedback(tab);

    const storageRef = getGroupMediaStorageReference({
      url: item.url,
      storageBucket: item.storageBucket,
      storagePath: item.storagePath,
    });
    if (storageRef) {
      await supabase.storage.from(storageRef.bucket).remove([storageRef.path]);
    }

    const { error } = await supabase.from('group_media').delete().eq('id', item.id);
    if (error) {
      setFeedback(tab, 'error', error.message || 'Unable to delete this file.');
      setDeletingMediaId(null);
      return;
    }

    setFeedback(tab, 'success', `${tab === 'docs' ? 'Document' : 'Media item'} deleted.`);
    setDeletingMediaId(null);
    await invalidateGroupSlice(['community-group-library', groupId]);
  };

  const handleCreateEvent = async () => {
    if (!groupId || !user?.id) return;
    if (!eventTitle.trim() || !eventStartDate || !eventEndDate) {
      setFeedback('calendar', 'error', 'Title, start date, and end date are required.');
      return;
    }

    setIsSubmittingEvent(true);
    clearFeedback('calendar');

    const { error } = await supabase.from('group_events').insert({
      group_id: groupId,
      title: eventTitle.trim(),
      description: eventDescriptionDraft.trim() || null,
      start_date: new Date(eventStartDate).toISOString(),
      end_date: new Date(eventEndDate).toISOString(),
      location: eventLocation.trim() || null,
      type: eventType,
      created_by: user.id,
      created_by_name: currentUserDisplayName,
      is_all_day: eventAllDay,
      max_attendees: null,
      current_attendees: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    if (error) {
      setFeedback('calendar', 'error', error.message || 'Unable to create this event.');
      setIsSubmittingEvent(false);
      return;
    }

    setEventTitle('');
    setEventDescriptionDraft('');
    setEventLocation('');
    setEventType('meeting');
    setEventStartDate('');
    setEventEndDate('');
    setEventAllDay(false);
    setFeedback('calendar', 'success', 'Event created.');
    setIsSubmittingEvent(false);
    await invalidateGroupSlice(['community-group-events', groupId]);
  };

  const handleDeleteEvent = async (event: GroupEventItem) => {
    if (isFacilitiesGoogleCalendarEvent(event)) return;
    if (!window.confirm(`Delete ${event.title}?`)) return;

    setDeletingEventId(event.id);
    clearFeedback('calendar');

    const { error } = await supabase.from('group_events').delete().eq('id', event.id);

    if (error) {
      setFeedback('calendar', 'error', error.message || 'Unable to delete this event.');
      setDeletingEventId(null);
      return;
    }

    setFeedback('calendar', 'success', 'Event deleted.');
    setDeletingEventId(null);
    await invalidateGroupSlice(['community-group-events', groupId]);
  };

  const handleCreateGoal = async () => {
    if (!groupId || !user?.id) return;
    if (!goalTitle.trim() || !goalDueDate) {
      setFeedback('goals', 'error', 'Goal title and due date are required.');
      return;
    }

    setIsSubmittingGoal(true);
    clearFeedback('goals');

    const { error } = await supabase.from('group_goals').insert({
      group_id: groupId,
      title: goalTitle.trim(),
      description: goalDescriptionDraft.trim() || null,
      due_date: new Date(`${goalDueDate}T00:00:00`).toISOString(),
      status: 'pending',
      created_by: user.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    if (error) {
      setFeedback('goals', 'error', error.message || 'Unable to create this goal.');
      setIsSubmittingGoal(false);
      return;
    }

    setGoalTitle('');
    setGoalDescriptionDraft('');
    setGoalDueDate('');
    setFeedback('goals', 'success', 'Goal created.');
    setIsSubmittingGoal(false);
    await invalidateGroupSlice(['community-group-goals', groupId]);
  };

  const handleUpdateGoalStatus = async (goal: GroupGoalItem, status: GroupGoalItem['status']) => {
    setUpdatingGoalId(goal.id);
    clearFeedback('goals');

    const payload = status === 'achieved'
      ? { status, achieved_at: new Date().toISOString(), achieved_by: user?.id ?? null }
      : { status, achieved_at: null, achieved_by: null };

    const { error } = await supabase.from('group_goals').update(payload).eq('id', goal.id);
    if (error) {
      setFeedback('goals', 'error', error.message || 'Unable to update this goal.');
      setUpdatingGoalId(null);
      return;
    }

    setFeedback('goals', 'success', 'Goal status updated.');
    setUpdatingGoalId(null);
    await invalidateGroupSlice(['community-group-goals', groupId]);
  };

  const handleDeleteGoal = async (goal: GroupGoalItem) => {
    if (!window.confirm(`Delete ${goal.title}?`)) return;

    setDeletingGoalId(goal.id);
    clearFeedback('goals');

    const { error } = await supabase.from('group_goals').delete().eq('id', goal.id);
    if (error) {
      setFeedback('goals', 'error', error.message || 'Unable to delete this goal.');
      setDeletingGoalId(null);
      return;
    }

    setFeedback('goals', 'success', 'Goal deleted.');
    setDeletingGoalId(null);
    await invalidateGroupSlice(['community-group-goals', groupId]);
  };

  const updateFeedPost = (postId: string, updater: (post: GroupFeedItem) => GroupFeedItem) => {
    queryClient.setQueryData<GroupFeedItem[] | undefined>(['community-group-feed', groupId], (current) =>
      Array.isArray(current) ? current.map((post) => (post.id === postId ? updater(post) : post)) : current
    );
  };

  const handleToggleGroupPostLike = async (post: GroupFeedItem) => {
    if (!user?.id) return;

    setLikingGroupPostId(post.id);
    clearFeedback('feed');

    const { data: existingLike, error: existingLikeError } = await supabase
      .from('group_post_likes')
      .select('post_id')
      .eq('post_id', post.id)
      .eq('user_id', user.id)
      .maybeSingle();

    if (existingLikeError) {
      setFeedback('feed', 'error', existingLikeError.message || 'Unable to update this like right now.');
      setLikingGroupPostId(null);
      return;
    }

    if (existingLike) {
      const { error } = await supabase
        .from('group_post_likes')
        .delete()
        .eq('post_id', post.id)
        .eq('user_id', user.id);

      if (error) {
        setFeedback('feed', 'error', error.message || 'Unable to remove your like right now.');
        setLikingGroupPostId(null);
        return;
      }

      updateFeedPost(post.id, (current) => ({
        ...current,
        viewerLiked: false,
        likesCount: Math.max(0, current.likesCount - 1),
      }));
    } else {
      const { error } = await supabase.from('group_post_likes').insert({
        post_id: post.id,
        user_id: user.id,
        created_at: new Date().toISOString(),
      });

      if (error) {
        setFeedback('feed', 'error', error.message || 'Unable to like this post right now.');
        setLikingGroupPostId(null);
        return;
      }

      updateFeedPost(post.id, (current) => ({
        ...current,
        viewerLiked: true,
        likesCount: current.likesCount + 1,
      }));
    }

    setLikingGroupPostId(null);
    void invalidateGroupSlice(['community-group-feed', groupId]);
  };

  const handleCreateGroupComment = async () => {
    if (!activeCommentPostId || !user?.id) return;
    const normalizedContent = groupCommentDraft.trim();
    if (!normalizedContent) {
      setFeedback('feed', 'error', 'Comment content is required.');
      return;
    }

    setIsSubmittingGroupComment(true);
    clearFeedback('feed');

    const { error } = await supabase.from('group_post_comments').insert({
      post_id: activeCommentPostId,
      author_id: user.id,
      author_name: currentUserDisplayName,
      content: normalizedContent,
      reply_to_id: null,
      status: 'published',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    if (error) {
      setFeedback('feed', 'error', error.message || 'Unable to post this comment right now.');
      setIsSubmittingGroupComment(false);
      return;
    }

    updateFeedPost(activeCommentPostId, (current) => ({
      ...current,
      commentsCount: current.commentsCount + 1,
    }));
    setGroupCommentDraft('');
    setFeedback('feed', 'success', 'Comment posted.');
    setIsSubmittingGroupComment(false);
    await invalidateGroupSlice(
      ['community-group-feed', groupId],
      ['community-group-post-comments', activeCommentPostId]
    );
  };

  const feedQuery = useQuery({
    queryKey: ['community-group-feed', groupId],
    enabled: Boolean(groupId) && activeTab === 'feed' && canViewGroupContent,
    queryFn: async () => {
      try {
        const { data: bundleRows, error: bundleError } = await supabase.rpc('get_group_feed_bundle', {
          p_group_id: groupId,
          p_limit: 20,
          p_offset: 0,
          p_include_engagement_preview: false,
          p_viewer_id: user?.id ?? null,
        });

        if (!bundleError && Array.isArray(bundleRows)) {
          const chronoPosts = bundleRows
            .map((row: any) => row?.payload ?? row)
            .filter((row: any) => !!row?.id)
            .map((post: any) => ({
              id: post.id,
              content: post.content || '',
              authorId: post.author_id,
              authorName: post.author?.full_name || post.author_name || 'Unknown user',
              type: post.type || 'post',
              status: post.status || 'published',
              createdAt: post.created_at,
              images: normalizeUrlArray(post.images),
              videoUrl: post.video_url || null,
              likesCount: Number(post.likes_count ?? 0),
              commentsCount: Number(post.comments_count ?? 0),
              seenCount:
                post.seen_count === null || typeof post.seen_count === 'undefined'
                  ? null
                  : Number(post.seen_count),
              viewerLiked: Boolean(post.viewer_liked),
              isPinned: Boolean(post.is_pinned),
              pinnedAt: post.pinned_at ?? null,
              editedAt: post.edited_at ?? null,
              originalPost: post.original_post ?? null,
              videoId: post.video_id ?? null,
            })) as GroupFeedItem[];

          try {
            const pinned = await fetchPinnedCommunityGroupPosts(groupId ?? '', 20);
            if (pinned.length > 0) {
              const pinnedIds = new Set(pinned.map((post) => post.id));
              const pinnedItems: GroupFeedItem[] = pinned.map((post) => {
                const existing = chronoPosts.find((entry) => entry.id === post.id);
                return {
                  id: post.id,
                  content: post.content || '',
                  authorId: post.author_id,
                  authorName: existing?.authorName || post.author_name || 'Unknown user',
                  type: post.type || 'post',
                  status: post.status || 'published',
                  createdAt: post.created_at,
                  images: post.images ?? [],
                  videoUrl: post.video_url || null,
                  likesCount: existing?.likesCount ?? 0,
                  commentsCount: existing?.commentsCount ?? 0,
                  seenCount: existing?.seenCount ?? null,
                  viewerLiked: existing?.viewerLiked ?? false,
                  isPinned: true,
                  pinnedAt: post.pinned_at ?? null,
                  editedAt: post.edited_at ?? null,
                  originalPost: post.original_post ?? null,
                  videoId: post.video_id ?? null,
                };
              });
              return [...pinnedItems, ...chronoPosts.filter((post) => !pinnedIds.has(post.id))];
            }
          } catch {
            // Keep chronological feed if pinned fetch fails.
          }

          return chronoPosts;
        }
      } catch {
      }

      const { data, error } = await supabase
        .from('group_posts')
        .select(
          'id, content, author_id, author_name, type, status, created_at, images, video_url, video_id, is_pinned, pinned_at, edited_at, original_post'
        )
        .eq('group_id', groupId)
        .or('status.is.null,status.eq.published')
        .or(`feed_visible_until.is.null,feed_visible_until.gt."${new Date().toISOString()}"`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      const posts = (data ?? []) as any[];
      const postIds = posts.map((post) => post.id).filter(Boolean);
      const [likesResult, commentsResult, viewerLikesResult, seenSummaryResult] = await Promise.all([
        postIds.length
          ? supabase.from('group_post_likes').select('post_id').in('post_id', postIds)
          : Promise.resolve({ data: [], error: null }),
        postIds.length
          ? supabase
              .from('group_post_comments')
              .select('post_id')
              .in('post_id', postIds)
              .or('status.is.null,status.eq.published')
          : Promise.resolve({ data: [], error: null }),
        postIds.length && user?.id
          ? supabase.from('group_post_likes').select('post_id').eq('user_id', user.id).in('post_id', postIds)
          : Promise.resolve({ data: [], error: null }),
        postIds.length
          ? supabase.rpc('get_group_post_seen_summaries', { p_post_ids: postIds })
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (likesResult.error) throw likesResult.error;
      if (commentsResult.error) throw commentsResult.error;
      if (viewerLikesResult.error) throw viewerLikesResult.error;
      if (seenSummaryResult.error) throw seenSummaryResult.error;

      const likesByPost = new Map<string, number>();
      for (const row of (likesResult.data ?? []) as Array<{ post_id: string }>) {
        likesByPost.set(row.post_id, (likesByPost.get(row.post_id) ?? 0) + 1);
      }

      const commentsByPost = new Map<string, number>();
      for (const row of (commentsResult.data ?? []) as Array<{ post_id: string }>) {
        commentsByPost.set(row.post_id, (commentsByPost.get(row.post_id) ?? 0) + 1);
      }

      const viewerLikedPostIds = new Set(
        ((viewerLikesResult.data ?? []) as Array<{ post_id: string }>).map((row) => row.post_id)
      );
      const seenSummaryByPostId = Object.fromEntries(
        ((seenSummaryResult.data ?? []) as Array<Record<string, unknown>>).map((row) => [
          String(row.post_id),
          row.seen_count === null || typeof row.seen_count === 'undefined'
            ? null
            : Number.isFinite(Number(row.seen_count))
              ? Number(row.seen_count)
              : null,
        ])
      );

      const chronoPosts = posts.map((post: any) => ({
        id: post.id,
        content: post.content || '',
        authorId: post.author_id,
        authorName: post.author_name || 'Unknown user',
        type: post.type || 'post',
        status: post.status || 'published',
        createdAt: post.created_at,
        images: normalizeUrlArray(post.images),
        videoUrl: post.video_url || null,
        likesCount: likesByPost.get(post.id) ?? 0,
        commentsCount: commentsByPost.get(post.id) ?? 0,
        seenCount: seenSummaryByPostId[post.id] ?? null,
        viewerLiked: viewerLikedPostIds.has(post.id),
        isPinned: Boolean(post.is_pinned),
        pinnedAt: post.pinned_at ?? null,
        editedAt: post.edited_at ?? null,
        originalPost: post.original_post ?? null,
        videoId: post.video_id ?? null,
      })) as GroupFeedItem[];

      try {
        const pinned = await fetchPinnedCommunityGroupPosts(groupId ?? '', 20);
        if (pinned.length > 0) {
          const pinnedIds = new Set(pinned.map((post) => post.id));
          const pinnedItems: GroupFeedItem[] = pinned.map((post) => {
            const existing = chronoPosts.find((entry) => entry.id === post.id);
            return {
              id: post.id,
              content: post.content || '',
              authorId: post.author_id,
              authorName: existing?.authorName || post.author_name || 'Unknown user',
              type: post.type || 'post',
              status: post.status || 'published',
              createdAt: post.created_at,
              images: post.images ?? [],
              videoUrl: post.video_url || null,
              likesCount: existing?.likesCount ?? 0,
              commentsCount: existing?.commentsCount ?? 0,
              seenCount: existing?.seenCount ?? null,
              viewerLiked: existing?.viewerLiked ?? false,
              isPinned: true,
              pinnedAt: post.pinned_at ?? null,
              editedAt: post.edited_at ?? existing?.editedAt ?? null,
              originalPost: post.original_post ?? existing?.originalPost ?? null,
              videoId: post.video_id ?? existing?.videoId ?? null,
            };
          });
          return [...pinnedItems, ...chronoPosts.filter((post) => !pinnedIds.has(post.id))];
        }
      } catch {
        // Keep chronological feed if pinned fetch fails.
      }

      return chronoPosts;
    },
  });

  const groupCommentsQuery = useQuery({
    queryKey: ['community-group-post-comments', activeCommentPostId],
    enabled: Boolean(activeCommentPostId) && activeTab === 'feed' && canViewGroupContent,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_post_comments')
        .select('id, post_id, content, created_at, author_id, author_name, reply_to_id, status')
        .eq('post_id', activeCommentPostId)
        .or('status.is.null,status.eq.published')
        .order('created_at', { ascending: true });

      if (error) throw error;

      return (data ?? []).map((comment: any) => ({
        id: comment.id,
        postId: comment.post_id,
        content: comment.content || '',
        authorId: comment.author_id,
        authorName: comment.author_name || 'Unknown user',
        createdAt: comment.created_at,
        replyToId: comment.reply_to_id,
      })) as GroupFeedCommentItem[];
    },
  });

  const calendarQuery = useQuery({
    queryKey: ['community-group-events', groupId],
    enabled: Boolean(groupId) && activeTab === 'calendar' && canViewGroupContent,
    queryFn: async () => {
      const googleEvents = await fetchFacilitiesGroupGoogleCalendarEvents(groupId ?? '');
      const mapNativeEvent = (event: any): GroupEventItem => ({
        id: event.id,
        title: event.title,
        description: event.description,
        startDate: event.start_date,
        endDate: event.end_date,
        location: event.location,
        type: event.type || 'other',
        createdBy: event.created_by,
        currentAttendees: event.current_attendees,
        maxAttendees: event.max_attendees,
        isAllDay: event.is_all_day,
      });

      try {
        const { data: rpcRows, error: rpcError } = await supabase.rpc('get_group_events_with_attendance', {
          p_group_id: groupId,
          p_start_date: null,
          p_end_date: null,
          p_type: null,
        });

        if (!rpcError && Array.isArray(rpcRows)) {
          const nativeEvents = rpcRows
            .map((row: any) => row?.payload ?? row)
            .filter((row: any) => !!row?.id)
            .map(mapNativeEvent);
          return mergeGroupCalendarEvents(nativeEvents, googleEvents);
        }
      } catch {
      }

      const { data, error } = await supabase
        .from('group_events')
        .select('id, title, description, start_date, end_date, location, type, created_by, current_attendees, max_attendees, is_all_day')
        .eq('group_id', groupId)
        .order('start_date', { ascending: true })
        .limit(20);

      if (error) {
        if (error.code === 'PGRST200' || error.message?.includes('group_events')) {
          return mergeGroupCalendarEvents([] as GroupEventItem[], googleEvents);
        }
        throw error;
      }

      return mergeGroupCalendarEvents((data ?? []).map(mapNativeEvent), googleEvents);
    },
  });

  const libraryQuery = useQuery({
    queryKey: ['community-group-library', groupId],
    enabled: Boolean(groupId) && (activeTab === 'media' || activeTab === 'docs') && canViewGroupContent,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_media')
        .select('id, type, filename, size, uploaded_at, uploaded_by, uploaded_by_name, description, url, storage_bucket, storage_path')
        .eq('group_id', groupId)
        .order('uploaded_at', { ascending: false })
        .limit(40);

      if (error) throw error;

      return await Promise.all(
        (data ?? []).map(async (item: any) => {
          let resolvedUrl: string | null = null;
          try {
            resolvedUrl = await resolveGroupMediaUrl({
              url: item.url,
              storageBucket: item.storage_bucket,
              storagePath: item.storage_path,
            });
          } catch (error) {
            console.warn('Failed to resolve group media URL', {
              id: item.id,
              storageBucket: item.storage_bucket,
              storagePath: item.storage_path,
              error,
            });
          }

          return {
            id: item.id,
            type: item.type || 'document',
            fileName: item.filename || 'Untitled file',
            fileSize: item.size,
            uploadedBy: item.uploaded_by,
            uploadedAt: item.uploaded_at,
            uploadedByName: item.uploaded_by_name,
            description: item.description,
            url: resolvedUrl,
            storageBucket: item.storage_bucket,
            storagePath: item.storage_path,
          } as GroupMediaItem;
        }),
      );
    },
  });

  const chatsQuery = useQuery({
    queryKey: ['community-group-chats', groupId],
    enabled: Boolean(groupId) && activeTab === 'chats' && canViewGroupContent,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_chats')
        .select('id, name, description, message_count, updated_at, is_private, is_active')
        .eq('group_id', groupId)
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(30);

      if (error) throw error;

      return (data ?? []).map((chat: any) => ({
        id: chat.id,
        name: chat.name || 'Group chat',
        description: chat.description,
        messageCount: Number(chat.message_count ?? 0),
        updatedAt: chat.updated_at,
        isPrivate: Boolean(chat.is_private),
      })) as GroupChatItem[];
    },
  });

  const goalsQuery = useQuery({
    queryKey: ['community-group-goals', groupId],
    enabled: Boolean(groupId) && activeTab === 'goals' && canViewGroupContent,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_goals')
        .select('id, title, description, due_date, status, created_by, created_at')
        .eq('group_id', groupId)
        .order('due_date', { ascending: true })
        .limit(20);

      if (error) throw error;

      return (data ?? []).map((goal: any) => ({
        id: goal.id,
        title: goal.title,
        description: goal.description,
        dueDate: goal.due_date,
        status: goal.status || 'pending',
        createdBy: goal.created_by,
        createdAt: goal.created_at,
      })) as GroupGoalItem[];
    },
  });

  const activeTabQuery =
    activeTab === 'feed'
      ? feedQuery
      : activeTab === 'calendar'
      ? calendarQuery
      : activeTab === 'media' || activeTab === 'docs'
      ? libraryQuery
      : activeTab === 'chats'
      ? chatsQuery
      : goalsQuery;

  const feedPosts = useMemo(() => feedQuery.data ?? [], [feedQuery.data]);
  const mediaItems = (libraryQuery.data ?? []).filter(
    (item) => item.type !== 'document' && item.type !== 'link',
  );
  const documentItems = (libraryQuery.data ?? []).filter(
    (item) => item.type === 'document' || item.type === 'link',
  );

  React.useEffect(() => {
    if (!user?.id || !isMember || activeTab !== 'feed' || feedPosts.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visiblePostIds = entries
          .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6)
          .map((entry) => (entry.target as HTMLElement).dataset.postId ?? '')
          .filter((postId) => postId.length > 0)
          .filter((postId) => !seenPostIdsRef.current.has(postId));

        if (visiblePostIds.length === 0) return;

        visiblePostIds.forEach((postId) => seenPostIdsRef.current.add(postId));
        recordSeen.mutate(visiblePostIds);
      },
      { threshold: [0.6] }
    );

    feedPosts.forEach((post) => {
      const node = seenCardRefs.current[post.id];
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [activeTab, feedPosts, isMember, recordSeen, user?.id]);

  const activeSeenPost = feedPosts.find((post) => post.id === activeSeenPostId) ?? null;

  const handleOpenGroupPost = (postId: string) => {
    navigate(`/groups/${groupId}/posts/${postId}`);
  };

  const renderTabContent = () => {
    const activeFeedback = tabFeedback[activeTab];

    if (!canViewGroupContent) {
      return (
        <EmptyState>
          {groupQuery.data?.is_private
            ? 'Join this private group to unlock the feed, chats, media, events, and goals.'
            : 'Group content is still loading.'}
        </EmptyState>
      );
    }

    if (activeTabQuery.isLoading) {
      return <EmptyState>Loading {activeTabMeta.label.toLowerCase()}...</EmptyState>;
    }

    if (activeTabQuery.error) {
      return <EmptyState>{getQueryErrorMessage(activeTabQuery.error)}</EmptyState>;
    }

    if (activeTab === 'feed') {
      const posts = feedQuery.data ?? [];
      return (
        <>
          {canCreateGroupContent ? (
            <ComposerCard>
              <SectionTitle>Create post</SectionTitle>
              <FormGrid>
                <Field>
                  <FieldLabel>Post type</FieldLabel>
                  <SelectField
                    value={feedType}
                    onChange={(event) => {
                      const nextType = event.target.value as typeof feedType;
                      setFeedType(nextType);
                      if (nextType === 'prayer') {
                        clearFeedMedia();
                      }
                    }}
                  >
                    <option value="post">Post</option>
                    <option value="announcement">Announcement</option>
                    <option value="prayer">Prayer</option>
                    <option value="praise">Praise</option>
                    <option value="testimony">Testimony</option>
                  </SelectField>
                </Field>
                <Field>
                  <FieldLabel>Message</FieldLabel>
                  <TextArea
                    value={feedDraft}
                    onChange={(event) => setFeedDraft(event.target.value)}
                    placeholder="Share an update with the group."
                  />
                </Field>
                {feedType !== 'prayer' ? (
                  <Field>
                    <FieldLabel>Photo / video</FieldLabel>
                    <MediaUploadRow>
                      <MediaButtonRow>
                        <FilePickerButton $disabled={isUploadingFeedImage || isUploadingFeedVideo || isSubmittingFeed}>
                          {isUploadingFeedImage ? 'Uploading photo...' : feedImageUrl ? 'Replace photo' : 'Add photo'}
                          <input
                            type="file"
                            accept="image/*"
                            disabled={isUploadingFeedImage || isUploadingFeedVideo || isSubmittingFeed}
                            onChange={(event) => {
                              const file = event.target.files?.[0] ?? null;
                              event.target.value = '';
                              void handleFeedImageSelected(file);
                            }}
                          />
                        </FilePickerButton>
                        <FilePickerButton $disabled={isUploadingFeedImage || isUploadingFeedVideo || isSubmittingFeed}>
                          {isUploadingFeedVideo ? 'Uploading video...' : feedVideoUrl ? 'Replace video' : 'Add video'}
                          <input
                            type="file"
                            accept="video/*"
                            disabled={isUploadingFeedImage || isUploadingFeedVideo || isSubmittingFeed}
                            onChange={(event) => {
                              const file = event.target.files?.[0] ?? null;
                              event.target.value = '';
                              void handleFeedVideoSelected(file);
                            }}
                          />
                        </FilePickerButton>
                      </MediaButtonRow>
                      <HelperText>
                        Photos up to {formatBytesAsMb(MAX_FEED_IMAGE_UPLOAD_BYTES)}MB. Videos up to{' '}
                        {formatBytesAsMb(MAX_VIDEO_UPLOAD_BYTES)}MB.
                      </HelperText>
                      {(feedImageUrl || feedVideoUrl) && (
                        <AttachmentList>
                          {feedImageUrl ? (
                            <AttachmentChip>
                              Photo attached
                              <AttachmentClearButton
                                type="button"
                                onClick={() => {
                                  setFeedImageUrl('');
                                  setFeedImageName('');
                                  setFeedImageSize(0);
                                }}
                              >
                                Remove
                              </AttachmentClearButton>
                            </AttachmentChip>
                          ) : null}
                          {feedVideoUrl ? (
                            <AttachmentChip>
                              Video attached
                              <AttachmentClearButton
                                type="button"
                                onClick={() => {
                                  setFeedVideoUrl('');
                                  setFeedVideoId('');
                                  setFeedVideoName('');
                                  setFeedVideoSize(0);
                                }}
                              >
                                Remove
                              </AttachmentClearButton>
                            </AttachmentChip>
                          ) : null}
                        </AttachmentList>
                      )}
                      {feedImageUrl ? <AttachmentPreview src={feedImageUrl} alt="Selected group post photo" /> : null}
                      {feedVideoUrl ? <AttachmentVideoPreview src={feedVideoUrl} controls preload="metadata" /> : null}
                    </MediaUploadRow>
                  </Field>
                ) : (
                  <HelperText>Prayer requests are text-only.</HelperText>
                )}
                {editingFeedPost ? (
                  <HelperText>Editing your post. The original version stays available from Edited.</HelperText>
                ) : (
                <Field>
                  <FieldLabel>
                    <input
                      type="checkbox"
                      checked={isFeedScheduled}
                      onChange={(event) => setIsFeedScheduled(event.target.checked)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    Schedule for later
                  </FieldLabel>
                  {isFeedScheduled ? (
                    <input
                      type="datetime-local"
                      value={feedScheduledFor}
                      min={new Date().toISOString().slice(0, 16)}
                      onChange={(event) => setFeedScheduledFor(event.target.value)}
                    />
                  ) : null}
                </Field>
                )}
                <ActionRow>
                  <ActionButton
                    type="button"
                    $tone="primary"
                    disabled={
                      isSubmittingFeed ||
                      isUploadingFeedImage ||
                      isUploadingFeedVideo
                    }
                    onClick={() => void handleCreatePost()}
                  >
                    {isSubmittingFeed
                      ? (editingFeedPost ? 'Saving...' : 'Publishing...')
                      : isUploadingFeedImage || isUploadingFeedVideo
                        ? 'Uploading media...'
                        : editingFeedPost
                          ? 'Save changes'
                          : isFeedScheduled
                            ? 'Schedule post'
                            : 'Publish post'}
                  </ActionButton>
                </ActionRow>
              </FormGrid>
            </ComposerCard>
          ) : (
            <ComposerCard>
              <SectionTitle>Feed actions</SectionTitle>
              <HelperText>Join this group if you want to publish posts into the shared feed.</HelperText>
            </ComposerCard>
          )}

          {activeFeedback ? <StatusBanner $tone={activeFeedback.tone}>{activeFeedback.text}</StatusBanner> : null}

          {posts.length ? (
            <PreviewList>
              {posts.map((post) => {
                const canDeletePost = canManageGroup || post.authorId === user?.id;
                const isCommentPanelOpen = activeCommentPostId === post.id;
                const feedComments = isCommentPanelOpen ? groupCommentsQuery.data ?? [] : [];
                return (
                  <PreviewItem
                    key={post.id}
                    ref={(node) => {
                      seenCardRefs.current[post.id] = node;
                    }}
                    data-post-id={post.id}
                    $interactive
                    $pinned={Boolean(post.isPinned)}
                    role="link"
                    tabIndex={0}
                    aria-label={`Open post by ${post.authorName}`}
                    onClick={() => handleOpenGroupPost(post.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOpenGroupPost(post.id);
                      }
                    }}
                  >
                    <PreviewTop>
                      <FeedCardLead>
                        <FeedCardHint>Open discussion</FeedCardHint>
                        <PreviewTitle>{post.authorName}</PreviewTitle>
                        <PreviewMeta>
                          {formatDateTimeLabel(post.createdAt)}
                          {isPostEdited(post) ? (
                            <>
                              {' · '}
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOriginalSnapshot(readOriginalPost(post));
                                }}
                                style={{ background: 'none', border: 0, padding: 0, color: 'inherit', fontWeight: 700, cursor: 'pointer' }}
                              >
                                Edited
                              </button>
                            </>
                          ) : null}
                        </PreviewMeta>
                      </FeedCardLead>
                      <InlineBadgeRow>
                        <InlineBadge $tone="highlight">{post.type}</InlineBadge>
                        <InlineBadge>{post.status || 'published'}</InlineBadge>
                        {post.isPinned ? <InlineBadge $tone="pinned">Pinned</InlineBadge> : null}
                        {post.images.length ? <InlineBadge>{post.images.length} image{post.images.length === 1 ? '' : 's'}</InlineBadge> : null}
                        {post.videoUrl ? <InlineBadge>Video</InlineBadge> : null}
                      </InlineBadgeRow>
                    </PreviewTop>
                    <PostRichContent
                      text={post.content || 'No post body provided.'}
                      images={post.images}
                      videoUrl={post.videoUrl}
                      imageAlt={`${post.authorName} post`}
                    />
                    <FeedActionRow>
                      <FeedStatRow>
                        <FeedStat>{post.likesCount} {post.likesCount === 1 ? 'like' : 'likes'}</FeedStat>
                        <FeedStat>{post.commentsCount} {post.commentsCount === 1 ? 'comment' : 'comments'}</FeedStat>
                        {canViewSeenStatus && post.seenCount !== null ? (
                          <FeedActionButton
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setActiveSeenPostId(post.id);
                            }}
                          >
                            Seen by {post.seenCount ?? 0}
                          </FeedActionButton>
                        ) : null}
                      </FeedStatRow>
                      <InlineActions>
                        <FeedActionButton
                          type="button"
                          $active={post.viewerLiked}
                          disabled={likingGroupPostId === post.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleToggleGroupPostLike(post);
                          }}
                        >
                          {likingGroupPostId === post.id ? 'Working...' : post.viewerLiked ? 'Liked' : 'Like'}
                        </FeedActionButton>
                        <FeedActionButton
                          type="button"
                          $active={isCommentPanelOpen}
                          onClick={(event) => {
                            event.stopPropagation();
                            setActiveCommentPostId((current) => (current === post.id ? null : post.id));
                            setGroupCommentDraft('');
                          }}
                        >
                          {isCommentPanelOpen ? 'Hide comments' : 'Comment'}
                        </FeedActionButton>
                        {canPinContent ? (
                          <ActionButton
                            type="button"
                            $tone="secondary"
                            disabled={pinningPostId === post.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleTogglePostPinned(post);
                            }}
                          >
                            {pinningPostId === post.id ? 'Working...' : post.isPinned ? 'Unpin' : 'Pin'}
                          </ActionButton>
                        ) : null}
                        {post.authorId === user?.id ? (
                          <ActionButton
                            type="button"
                            $tone="secondary"
                            onClick={(event) => {
                              event.stopPropagation();
                              setEditingFeedPost(post);
                              setFeedDraft(post.content || '');
                              setFeedType(
                                post.type === 'announcement' || post.type === 'prayer' || post.type === 'praise' || post.type === 'testimony'
                                  ? post.type
                                  : 'post'
                              );
                              setFeedImageUrl(post.images[0] || '');
                              setFeedVideoUrl(post.videoUrl || '');
                              setFeedVideoId(post.videoId || '');
                              setIsFeedScheduled(false);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                          >
                            Edit
                          </ActionButton>
                        ) : null}
                        {canDeletePost ? (
                        <ActionButton
                          type="button"
                          $tone="secondary"
                          disabled={deletingPostId === post.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDeletePost(post);
                          }}
                        >
                          {deletingPostId === post.id ? 'Deleting...' : 'Delete'}
                        </ActionButton>
                        ) : null}
                      </InlineActions>
                    </FeedActionRow>
                    {isCommentPanelOpen ? (
                      <CommentPanel onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                        <SectionTitle>Comments</SectionTitle>
                        {groupCommentsQuery.isLoading ? <HelperText>Loading comments...</HelperText> : null}
                        {groupCommentsQuery.error ? (
                          <HelperText>{getQueryErrorMessage(groupCommentsQuery.error)}</HelperText>
                        ) : null}
                        {!groupCommentsQuery.isLoading && !groupCommentsQuery.error ? (
                          <>
                            {feedComments.length ? (
                              <CommentList>
                                {feedComments.map((comment) => (
                                  <CommentItem key={comment.id}>
                                    <CommentMeta>
                                      {comment.authorName} • {formatDateTimeLabel(comment.createdAt)}
                                    </CommentMeta>
                                    <CommentBody>{comment.content}</CommentBody>
                                  </CommentItem>
                                ))}
                              </CommentList>
                            ) : (
                              <HelperText>No comments yet. Start the conversation.</HelperText>
                            )}
                            {canCreateGroupContent ? (
                              <CommentComposer>
                                <TextArea
                                  value={groupCommentDraft}
                                  onChange={(event) => setGroupCommentDraft(event.target.value)}
                                  placeholder="Add a comment to this post."
                                />
                                <ActionRow>
                                  <ActionButton
                                    type="button"
                                    $tone="primary"
                                    disabled={isSubmittingGroupComment}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      void handleCreateGroupComment();
                                    }}
                                  >
                                    {isSubmittingGroupComment ? 'Posting...' : 'Post comment'}
                                  </ActionButton>
                                </ActionRow>
                              </CommentComposer>
                            ) : (
                              <HelperText>Join this group if you want to add comments.</HelperText>
                            )}
                          </>
                        ) : null}
                      </CommentPanel>
                    ) : null}
                  </PreviewItem>
                );
              })}
            </PreviewList>
          ) : (
            <EmptyState>No feed posts yet.</EmptyState>
          )}
        </>
      );
    }

    if (activeTab === 'calendar') {
      const events = calendarQuery.data ?? [];
      return (
        <>
          {canCreateGroupContent ? (
            <ComposerCard>
              <SectionTitle>Create event</SectionTitle>
              <FormGrid>
                <FormRow>
                  <Field>
                    <FieldLabel>Title</FieldLabel>
                    <TextInput value={eventTitle} onChange={(event) => setEventTitle(event.target.value)} placeholder="Prayer night" />
                  </Field>
                  <Field>
                    <FieldLabel>Type</FieldLabel>
                    <SelectField value={eventType} onChange={(event) => setEventType(event.target.value as typeof eventType)}>
                      <option value="meeting">Meeting</option>
                      <option value="prayer">Prayer</option>
                      <option value="study">Study</option>
                      <option value="fellowship">Fellowship</option>
                      <option value="outreach">Outreach</option>
                      <option value="worship">Worship</option>
                      <option value="other">Other</option>
                    </SelectField>
                  </Field>
                </FormRow>
                <Field>
                  <FieldLabel>Description</FieldLabel>
                  <TextArea value={eventDescriptionDraft} onChange={(event) => setEventDescriptionDraft(event.target.value)} placeholder="What should members expect?" />
                </Field>
                <FormRow>
                  <Field>
                    <FieldLabel>Location</FieldLabel>
                    <TextInput value={eventLocation} onChange={(event) => setEventLocation(event.target.value)} placeholder="Main campus" />
                  </Field>
                  <Field>
                    <FieldLabel>Start</FieldLabel>
                    <TextInput type="datetime-local" value={eventStartDate} onChange={(event) => setEventStartDate(event.target.value)} />
                  </Field>
                  <Field>
                    <FieldLabel>End</FieldLabel>
                    <TextInput type="datetime-local" value={eventEndDate} onChange={(event) => setEventEndDate(event.target.value)} />
                  </Field>
                </FormRow>
                <CheckboxRow>
                  <input type="checkbox" checked={eventAllDay} onChange={(event) => setEventAllDay(event.target.checked)} />
                  All-day event
                </CheckboxRow>
                <ActionRow>
                  <ActionButton type="button" $tone="primary" disabled={isSubmittingEvent} onClick={() => void handleCreateEvent()}>
                    {isSubmittingEvent ? 'Creating...' : 'Create event'}
                  </ActionButton>
                </ActionRow>
              </FormGrid>
            </ComposerCard>
          ) : (
            <ComposerCard>
              <SectionTitle>Calendar actions</SectionTitle>
              <HelperText>Join this group if you want to schedule events for members.</HelperText>
            </ComposerCard>
          )}

          {activeFeedback ? <StatusBanner $tone={activeFeedback.tone}>{activeFeedback.text}</StatusBanner> : null}

          {events.length ? (
            <PreviewList>
              {events.map((event) => {
                const isGoogleEvent = isFacilitiesGoogleCalendarEvent(event);
                const canDeleteEvent = !isGoogleEvent && (canManageGroup || event.createdBy === user?.id);
                return (
                  <PreviewItem key={event.id}>
                    <PreviewTop>
                      <div>
                        <PreviewTitle>{event.title}</PreviewTitle>
                        <PreviewMeta>{formatDateRange(event.startDate, event.endDate, event.isAllDay)}</PreviewMeta>
                      </div>
                      <InlineBadgeRow>
                        <InlineBadge $tone={isGoogleEvent ? 'warning' : 'highlight'}>
                          {isGoogleEvent ? 'Google Calendar' : event.type}
                        </InlineBadge>
                        {event.location ? <InlineBadge>{event.location}</InlineBadge> : null}
                        {!isGoogleEvent && (event.currentAttendees || event.maxAttendees) ? (
                          <InlineBadge>
                            {event.currentAttendees ?? 0}
                            {event.maxAttendees ? `/${event.maxAttendees}` : ''} attending
                          </InlineBadge>
                        ) : null}
                      </InlineBadgeRow>
                    </PreviewTop>
                    {event.description ? <PreviewBody>{event.description}</PreviewBody> : null}
                    {canDeleteEvent ? (
                      <InlineActions>
                        <ActionButton
                          type="button"
                          $tone="secondary"
                          disabled={deletingEventId === event.id}
                          onClick={() => void handleDeleteEvent(event)}
                        >
                          {deletingEventId === event.id ? 'Deleting...' : 'Delete'}
                        </ActionButton>
                      </InlineActions>
                    ) : null}
                  </PreviewItem>
                );
              })}
            </PreviewList>
          ) : (
            <EmptyState>No upcoming group events yet.</EmptyState>
          )}
        </>
      );
    }

    if (activeTab === 'media') {
      return (
        <>
          {canCreateGroupContent ? (
            <ComposerCard>
              <SectionTitle>Upload media</SectionTitle>
              <FormGrid>
                <Field>
                  <FieldLabel>File</FieldLabel>
                  <TextInput
                    type="file"
                    accept="image/*,video/*"
                    onChange={(event) => setMediaFile(event.target.files?.[0] ?? null)}
                  />
                  <HelperText>Videos up to {formatBytesAsMb(MAX_VIDEO_UPLOAD_BYTES)}MB.</HelperText>
                </Field>
                <Field>
                  <FieldLabel>Description</FieldLabel>
                  <TextArea
                    value={mediaDescriptionDraft}
                    onChange={(event) => setMediaDescriptionDraft(event.target.value)}
                    placeholder="Add context for this upload."
                  />
                </Field>
                <ActionRow>
                  <ActionButton type="button" $tone="primary" disabled={isUploadingMedia} onClick={() => void handleUploadMedia()}>
                    {isUploadingMedia ? 'Uploading...' : 'Upload media'}
                  </ActionButton>
                </ActionRow>
              </FormGrid>
            </ComposerCard>
          ) : (
            <ComposerCard>
              <SectionTitle>Media actions</SectionTitle>
              <HelperText>Join this group if you want to upload photos and videos here.</HelperText>
            </ComposerCard>
          )}

          {activeFeedback ? <StatusBanner $tone={activeFeedback.tone}>{activeFeedback.text}</StatusBanner> : null}

          {mediaItems.length ? (
            <PreviewList>
              {mediaItems.map((item) => {
                const canDeleteMediaItem = canManageGroup || item.uploadedBy === user?.id;
                return (
                  <PreviewItem key={item.id}>
                    <PreviewTop>
                      <div>
                        <PreviewTitle>{item.fileName}</PreviewTitle>
                        <PreviewMeta>
                          Uploaded {formatDateTimeLabel(item.uploadedAt)}
                          {item.uploadedByName ? ` by ${item.uploadedByName}` : ''}
                        </PreviewMeta>
                      </div>
                      <InlineBadgeRow>
                        <InlineBadge $tone="highlight">{item.type}</InlineBadge>
                        <InlineBadge>{formatBytes(item.fileSize)}</InlineBadge>
                      </InlineBadgeRow>
                    </PreviewTop>
                    {item.description ? <PreviewBody>{item.description}</PreviewBody> : null}
                    {item.type === 'image' && item.url ? (
                      <img
                        src={item.url}
                        alt={item.fileName}
                        style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 16 }}
                      />
                    ) : null}
                    <InlineActions>
                      {item.url ? (
                        <ActionButton as="a" href={item.url} target="_blank" rel="noreferrer" $tone="primary">
                          Open
                        </ActionButton>
                      ) : (
                        <HelperText>File unavailable (missing storage object).</HelperText>
                      )}
                      {canDeleteMediaItem ? (
                        <ActionButton
                          type="button"
                          $tone="secondary"
                          disabled={deletingMediaId === item.id}
                          onClick={() => void handleDeleteMedia(item, 'media')}
                        >
                          {deletingMediaId === item.id ? 'Deleting...' : 'Delete'}
                        </ActionButton>
                      ) : null}
                    </InlineActions>
                  </PreviewItem>
                );
              })}
            </PreviewList>
          ) : (
            <EmptyState>No shared media uploads yet.</EmptyState>
          )}
        </>
      );
    }

    if (activeTab === 'docs') {
      return (
        <>
          {canCreateGroupContent ? (
            <ComposerCard>
              <SectionTitle>Upload document</SectionTitle>
              <FormGrid>
                <Field>
                  <FieldLabel>File</FieldLabel>
                  <TextInput
                    type="file"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
                    onChange={(event) => setDocumentFile(event.target.files?.[0] ?? null)}
                  />
                </Field>
                <Field>
                  <FieldLabel>Description</FieldLabel>
                  <TextArea
                    value={documentDescriptionDraft}
                    onChange={(event) => setDocumentDescriptionDraft(event.target.value)}
                    placeholder="Add context for this file."
                  />
                </Field>
                <ActionRow>
                  <ActionButton type="button" $tone="primary" disabled={isUploadingDocument} onClick={() => void handleUploadDocument()}>
                    {isUploadingDocument ? 'Uploading...' : 'Upload document'}
                  </ActionButton>
                </ActionRow>
              </FormGrid>
            </ComposerCard>
          ) : (
            <ComposerCard>
              <SectionTitle>Document actions</SectionTitle>
              <HelperText>Join this group if you want to upload files into the shared library.</HelperText>
            </ComposerCard>
          )}

          {activeFeedback ? <StatusBanner $tone={activeFeedback.tone}>{activeFeedback.text}</StatusBanner> : null}

          {documentItems.length ? (
            <PreviewList>
              {documentItems.map((item) => {
                const canDeleteDocument = canManageGroup || item.uploadedBy === user?.id;
                return (
                  <PreviewItem key={item.id}>
                    <PreviewTop>
                      <div>
                        <PreviewTitle>{item.fileName}</PreviewTitle>
                        <PreviewMeta>
                          Uploaded {formatDateTimeLabel(item.uploadedAt)}
                          {item.uploadedByName ? ` by ${item.uploadedByName}` : ''}
                        </PreviewMeta>
                      </div>
                      <InlineBadgeRow>
                        <InlineBadge $tone="highlight">Document</InlineBadge>
                        <InlineBadge>{formatBytes(item.fileSize)}</InlineBadge>
                      </InlineBadgeRow>
                    </PreviewTop>
                    {item.description ? <PreviewBody>{item.description}</PreviewBody> : null}
                    <InlineActions>
                      {item.url ? (
                        <ActionButton as="a" href={item.url} target="_blank" rel="noreferrer" $tone="primary">
                          {item.type === 'link' ? 'Open link' : 'Open'}
                        </ActionButton>
                      ) : (
                        <HelperText>File unavailable (missing storage object).</HelperText>
                      )}
                      {canDeleteDocument ? (
                        <ActionButton
                          type="button"
                          $tone="secondary"
                          disabled={deletingMediaId === item.id}
                          onClick={() => void handleDeleteMedia(item, 'docs')}
                        >
                          {deletingMediaId === item.id ? 'Deleting...' : 'Delete'}
                        </ActionButton>
                      ) : null}
                    </InlineActions>
                  </PreviewItem>
                );
              })}
            </PreviewList>
          ) : (
            <EmptyState>No shared documents yet.</EmptyState>
          )}
        </>
      );
    }

    if (activeTab === 'chats') {
      const chats = chatsQuery.data ?? [];
      if (!chats.length) {
        return <EmptyState>No active chat rooms yet.</EmptyState>;
      }
      return (
        <PreviewList>
          {chats.map((chat) => (
            <PreviewItem key={chat.id}>
              <PreviewTop>
                <div>
                  <PreviewTitle>{chat.name}</PreviewTitle>
                  <PreviewMeta>Updated {formatDateTimeLabel(chat.updatedAt)}</PreviewMeta>
                </div>
                <InlineBadgeRow>
                  <InlineBadge $tone={chat.isPrivate ? 'warning' : 'highlight'}>
                    {chat.isPrivate ? 'Private chat' : 'Open chat'}
                  </InlineBadge>
                  <InlineBadge>{chat.messageCount} messages</InlineBadge>
                </InlineBadgeRow>
              </PreviewTop>
              <PreviewBody>{chat.description?.trim() || 'No chat description provided.'}</PreviewBody>
              <InlineActions>
                <ActionButton type="button" $tone="primary" onClick={() => navigate(`/groups/${groupId}/chats/${chat.id}`)}>
                  Open thread
                </ActionButton>
              </InlineActions>
            </PreviewItem>
          ))}
        </PreviewList>
      );
    }

    const goals = goalsQuery.data ?? [];
    return (
      <>
        {canCreateGroupContent ? (
          <ComposerCard>
            <SectionTitle>Create goal</SectionTitle>
            <FormGrid>
              <FormRow>
                <Field>
                  <FieldLabel>Title</FieldLabel>
                  <TextInput value={goalTitle} onChange={(event) => setGoalTitle(event.target.value)} placeholder="Serve 12 families this month" />
                </Field>
                <Field>
                  <FieldLabel>Due date</FieldLabel>
                  <TextInput type="date" value={goalDueDate} onChange={(event) => setGoalDueDate(event.target.value)} />
                </Field>
              </FormRow>
              <Field>
                <FieldLabel>Description</FieldLabel>
                <TextArea value={goalDescriptionDraft} onChange={(event) => setGoalDescriptionDraft(event.target.value)} placeholder="What does success look like?" />
              </Field>
              <ActionRow>
                <ActionButton type="button" $tone="primary" disabled={isSubmittingGoal} onClick={() => void handleCreateGoal()}>
                  {isSubmittingGoal ? 'Creating...' : 'Create goal'}
                </ActionButton>
              </ActionRow>
            </FormGrid>
          </ComposerCard>
        ) : (
          <ComposerCard>
            <SectionTitle>Goal actions</SectionTitle>
            <HelperText>Join this group if you want to create and update shared goals.</HelperText>
          </ComposerCard>
        )}

        {activeFeedback ? <StatusBanner $tone={activeFeedback.tone}>{activeFeedback.text}</StatusBanner> : null}

        {goals.length ? (
          <PreviewList>
            {goals.map((goal) => {
              const canDeleteGoal = canManageGroup || goal.createdBy === user?.id;
              return (
                <PreviewItem key={goal.id}>
                  <PreviewTop>
                    <div>
                      <PreviewTitle>{goal.title}</PreviewTitle>
                      <PreviewMeta>Due {formatDateLabel(goal.dueDate)}</PreviewMeta>
                    </div>
                    <InlineBadgeRow>
                      <InlineBadge $tone={getGoalTone(goal.status)}>{goal.status.replace('_', ' ')}</InlineBadge>
                      <InlineBadge>Created {formatDateLabel(goal.createdAt)}</InlineBadge>
                    </InlineBadgeRow>
                  </PreviewTop>
                  {goal.description ? <PreviewBody>{goal.description}</PreviewBody> : null}
                  {canCreateGroupContent ? (
                    <InlineActions>
                      <ActionButton
                        type="button"
                        $tone="secondary"
                        disabled={updatingGoalId === goal.id}
                        onClick={() => void handleUpdateGoalStatus(goal, 'pending')}
                      >
                        Pending
                      </ActionButton>
                      <ActionButton
                        type="button"
                        $tone="secondary"
                        disabled={updatingGoalId === goal.id}
                        onClick={() => void handleUpdateGoalStatus(goal, 'achieved')}
                      >
                        Achieved
                      </ActionButton>
                      <ActionButton
                        type="button"
                        $tone="secondary"
                        disabled={updatingGoalId === goal.id}
                        onClick={() => void handleUpdateGoalStatus(goal, 'not_achieved')}
                      >
                        Not achieved
                      </ActionButton>
                      {canDeleteGoal ? (
                        <ActionButton
                          type="button"
                          $tone="secondary"
                          disabled={deletingGoalId === goal.id}
                          onClick={() => void handleDeleteGoal(goal)}
                        >
                          {deletingGoalId === goal.id ? 'Deleting...' : 'Delete'}
                        </ActionButton>
                      ) : null}
                    </InlineActions>
                  ) : null}
                </PreviewItem>
              );
            })}
          </PreviewList>
        ) : (
          <EmptyState>No group goals yet.</EmptyState>
        )}
      </>
    );
  };

  return (
    <Page>
      <Shell>
        <HeaderCard>
          <HeaderTop>
            <HeaderText>
              <Eyebrow>Community group</Eyebrow>
              <Title>{groupQuery.data?.name ?? 'Loading group...'}</Title>
              <Description>
                {groupQuery.data?.description?.trim() || 'This group does not have a description yet.'}
              </Description>
            </HeaderText>

            <HeaderActions>
              <BackButton type="button" onClick={() => navigate('/groups')}>
                Back to groups
              </BackButton>
              {!isMember && !groupQuery.data?.is_private ? (
                <ActionButton
                  type="button"
                  $tone="primary"
                  disabled={membershipBusyAction !== null}
                  onClick={() => void handleJoinGroup()}
                >
                  {membershipBusyAction === 'join' ? 'Joining...' : 'Join group'}
                </ActionButton>
              ) : null}
              {!isMember && groupQuery.data?.is_private ? (
                <ActionButton
                  type="button"
                  $tone="primary"
                  disabled={membershipBusyAction !== null || hasPendingJoinRequest}
                  onClick={() => void handleRequestToJoinGroup()}
                >
                  {hasPendingJoinRequest
                    ? 'Request pending'
                    : membershipBusyAction === 'request'
                    ? 'Sending request...'
                    : 'Request to join'}
                </ActionButton>
              ) : null}
              {isMember ? (
                <ActionButton
                  type="button"
                  $tone="secondary"
                  disabled={membershipBusyAction !== null}
                  onClick={() => void handleLeaveGroup()}
                >
                  {membershipBusyAction === 'leave' ? 'Leaving...' : 'Leave group'}
                </ActionButton>
              ) : null}
              {canOpenAdminSettings && groupId ? (
                <ActionButton type="button" $tone="primary" onClick={() => navigate(`/groups/${groupId}/manage`)}>
                  Admin settings
                </ActionButton>
              ) : null}
            </HeaderActions>
          </HeaderTop>

          {membershipSuccess || membershipError || hasPendingJoinRequest ? (
            <StatusStack>
              {membershipSuccess ? <StatusBanner $tone="success">{membershipSuccess}</StatusBanner> : null}
              {membershipError ? <StatusBanner $tone="error">{membershipError}</StatusBanner> : null}
              {!membershipSuccess && !membershipError && hasPendingJoinRequest ? (
                <StatusBanner $tone="info">Your request to join this private group is pending admin review.</StatusBanner>
              ) : null}
            </StatusStack>
          ) : null}

          <StatGrid>
            <StatCard>
              <StatLabel>Category</StatLabel>
              <StatValue>{getCategoryLabel(groupQuery.data?.category)}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Visibility</StatLabel>
              <StatValue>{groupQuery.data?.is_private ? 'Private group' : 'Public group'}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Members</StatLabel>
              <StatValue>{getMembersLabelFromCount(liveMemberCount, groupQuery.data?.max_members)}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Your access</StatLabel>
              <StatValue>
                {membershipQuery.data?.role
                  ? membershipQuery.data.role
                  : canManageGroup
                  ? 'Admin override'
                  : isMember
                  ? 'Member'
                  : hasPendingJoinRequest
                  ? 'Pending request'
                  : 'Viewer'}
              </StatValue>
            </StatCard>
          </StatGrid>

          <TabStrip>
            {GROUP_DETAIL_TABS.map((tab) => (
              <TabButton
                key={tab.key}
                type="button"
                $active={tab.key === activeTab}
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  if (tab.key === 'feed') {
                    next.delete('tab');
                  } else {
                    next.set('tab', tab.key);
                  }
                  setSearchParams(next);
                }}
              >
                {tab.label}
              </TabButton>
            ))}
          </TabStrip>
        </HeaderCard>

        {groupQuery.isLoading ? <EmptyState>Loading group details...</EmptyState> : null}
        {groupQuery.error ? <EmptyState>Unable to load this group right now.</EmptyState> : null}

        {!groupQuery.isLoading && !groupQuery.error ? (
          <ContentCard>
            <ContentLayout>
              <ContentIntro>
                <SectionTitle>{activeTabMeta.label}</SectionTitle>
                <SectionBody>{activeTabMeta.summary}</SectionBody>
                <MetaRow>
                  <MetaBadge>{groupQuery.data?.name ?? 'Group'}</MetaBadge>
                  <MetaBadge>{activeTabMeta.label} workspace</MetaBadge>
                </MetaRow>
              </ContentIntro>
              <ContentMain>{renderTabContent()}</ContentMain>
            </ContentLayout>
          </ContentCard>
        ) : null}

        <OriginalPostDialog snapshot={originalSnapshot} onClose={() => setOriginalSnapshot(null)} />
        <SeenByDialog
          open={Boolean(activeSeenPostId)}
          title={activeSeenPost ? `Seen by ${activeSeenPost.seenCount ?? 0}` : 'Seen by'}
          items={seenList ?? []}
          loading={seenListLoading}
          onClose={() => setActiveSeenPostId(null)}
        />
      </Shell>
    </Page>
  );
};

export default CommunityGroupDetailPage;